import React, {useEffect, useState, useCallback, useRef, useMemo} from "react";
import {useRouter} from "next/router";
import {LanguageOption, TagOption} from "@components/elements/FiltersToolbar";
import isEqual from "lodash.isequal";
import {ParsedUrlQueryInput} from "querystring";
import {GetServerSidePropsContext} from "next";
import cx from "classnames";
import MultiSelectField from "@components/elements/MultiSelectField";
import {IProcessedPluginData} from "@hooks/plugins/IProcessedPluginsData";
import {ResetFilterButton} from "@components/elements/ResetFilterButton";
import {FiltersModal} from "@components/elements/FiltersModal";
import {ModalHeader} from "@components/elements/ModalHeader";
import {ModalBody} from "@components/elements/ModalBody";
import {TextField} from "@components/elements/TextField";
import {StarRatingFilter} from "@components/elements/StarRatingFilter";
import {NumberFilter} from "@components/elements/NumberFilter";
import {IPluginsProps} from "@components/widgets/PluginsTable";
import Image from "next/image";
import Link from "@components/elements/DocumentLink";
import {StarRating} from "@components/elements/StarRating";
import {PageNavigation} from "@components/elements/PageNavigation";
import {NoResults} from "@components/elements/NoResults";
import {Chip} from "@components/elements/Chip";
import {Loading} from "@components/elements/Loading";
import {IPluginsQuery} from "@hooks/plugins/PluginsDataHooks";
import {TableSortingOption} from "@components/elements/tables/TableSortingOption";
import {Table} from "@components/elements/Table";
import {TableHeader} from "@components/elements/TableHeader";
import {TableHeaderColumn} from "@components/elements/TableHeaderColumn";
import {TableBody} from "@components/elements/TableBody";
import {TableRow} from "@components/elements/TableRow";
import {TableCell} from "@components/elements/TableCell";
import {ssrUsePluginsSearch, usePluginsSearch} from "@hooks/plugins/usePluginsData";
import {useStableNow} from "@hooks/useStableNow";
import {
    FaCalendar,
    FaColumns,
    FaDownload,
    FaHistory,
    FaListUl,
    FaPhp,
    FaPlug,
    FaTable,
    FaTag,
    FaThLarge,
    FaUsers
} from "react-icons/fa";
import {IoFilterCircleSharp} from "react-icons/io5";
import {DiWordpress} from "react-icons/di";
import {BiLinkExternal} from "react-icons/bi";
import {DatePicker} from "@components/elements/DatePicker";
import {FilterChipConfig, FilterChipGroup} from "@components/elements/FilterChipPopover";
import {getAuthorPageHref, getPluginAuthorSlug} from "@utils/wordpress";
import {
    formatDate as formatDateLocale,
    formatDateTime as formatDateTimeLocale,
    formatNumber as formatNumberLocale,
    formatPrice
} from "@utils/format";
import {
    clearPluginsListState,
    getPluginsListScrollContainer,
    normalizePluginsListUrl,
    readPluginsListState,
    writePluginsListState
} from "@utils/pluginsListState";
import {SiWoocommerce} from "react-icons/si";

export const DEFAULT_RESULTS_PER_PAGE = 25;
export const DEFAULT_MIN_RATING_FILTER_VALUE = 0;
export const DEFAULT_MAX_RATING_FILTER_VALUE = 5;
export const DEFAULT_MIN_DOWNLOADS_FILTER_VALUE = 0;
export const DEFAULT_MAX_DOWNLOADS_FILTER_VALUE = 500000000;
export const DEFAULT_MIN_ACTIVE_INSTALLS_FILTER_VALUE = 0;
export const DEFAULT_MAX_ACTIVE_INSTALLS_FILTER_VALUE = 5000000;
export const DEFAULT_MIN_TOTAL_RATINGS_FILTER_VALUE = 0;
export const DEFAULT_MAX_TOTAL_RATINGS_FILTER_VALUE = 15000;
export const MAX_SHORT_DESCRIPTION_LENGTH = 300;
export const DEFAULT_SORT_COLUMN = 'active_installs';
export const DEFAULT_SORT_DESCENDING = true;
export const DEFAULT_MIN_LAST_UPDATED_FILTER_VALUE = '';
export const DEFAULT_MAX_LAST_UPDATED_FILTER_VALUE = '';
export const DEFAULT_MIN_AGE_FILTER_VALUE = 0; // years
export const DEFAULT_MAX_AGE_FILTER_VALUE = 50; // years
export const DEFAULT_MIN_ADDED_FILTER_VALUE = '';
export const DEFAULT_MAX_ADDED_FILTER_VALUE = '';
type PluginsLayout = 'comfortable' | 'compact' | 'table';
const DEFAULT_LAYOUT: PluginsLayout = 'comfortable';
const isValidLayout = (value?: string): value is PluginsLayout =>
    value === 'comfortable' || value === 'compact' || value === 'table';
const COMPACT_DESCRIPTION_LENGTH = 140;
const SSR_MAX_DESCRIPTION_LENGTH = 600;
const SSR_MAX_ICONS = 1;
type TableColumnId =
    | 'name'
    | 'price'
    | 'rating'
    | 'downloaded'
    | 'active_installs'
    | 'num_ratings'
    | 'last_updated'
    | 'tags'
    | 'languages'
    | 'age'
    | 'installs_per_rating'
    | 'installs_per_day'
    | 'downloads_per_rating'
    | 'downloads_per_day'
    | 'reviews_per_day'
    | 'activation_rate';
const ALL_TABLE_COLUMNS: TableColumnId[] = [
    'name',
    'price',
    'rating',
    'num_ratings',
    'downloaded',
    'active_installs',
    'installs_per_rating',
    'installs_per_day',
    'downloads_per_rating',
    'downloads_per_day',
    'reviews_per_day',
    'activation_rate',
    'age',
    'last_updated',
    'tags',
    'languages',
];
const DEFAULT_TABLE_COLUMNS: TableColumnId[] = [...ALL_TABLE_COLUMNS];
const REQUIRED_TABLE_COLUMNS: TableColumnId[] = ['name'];
type TableColumnDefinition = {
    id: TableColumnId;
    label: string;
    sortable?: boolean;
    defaultDescending?: boolean;
    headerClassName?: string;
    contentClassName?: string;
    cellClassName?: string;
    isFilterable?: boolean;
    isFiltered?: boolean;
    sortType?: 'string' | 'number' | 'date';
    getSortValue?: (plugin: IProcessedPluginData) => string | number | Date | null | undefined;
    renderCell: (plugin: IProcessedPluginData) => React.ReactNode;
};

const getSortDirectionLabel = (sortType: TableColumnDefinition['sortType'], descending: boolean) => {
    if (sortType === 'string') {
        return descending ? 'Z to A' : 'A to Z';
    }
    if (sortType === 'date') {
        return descending ? 'new to old' : 'old to new';
    }
    return descending ? 'high to low' : 'low to high';
};

const API_SORTABLE_COLUMNS = new Set<TableColumnId | string>([
    ...ALL_TABLE_COLUMNS,
]);

const getMarketplaceLabel = (marketplace?: string) => marketplace === 'woocommerce' ? 'WooCommerce' : 'WordPress';
const getMarketplaceListingLabel = (marketplace?: string) => marketplace === 'woocommerce' ? 'WooCommerce Marketplace' : 'WordPress.org';

const getApiSort = (column?: string, descending: boolean = DEFAULT_SORT_DESCENDING) => {
    if (!column || !API_SORTABLE_COLUMNS.has(column)) {
        return {
            column: DEFAULT_SORT_COLUMN,
            descending: DEFAULT_SORT_DESCENDING,
        };
    }
    return {column, descending};
};

const yearsToDays = (years?: number | null) => {
    if (years === null || years === undefined || !isFinite(years)) return undefined;
    return Math.round(years * 365);
};

type PresetValue<T> = T | (() => T);

type PresetFilters = {
    searchFilterValue?: PresetValue<string>;
    nameFilterValue?: PresetValue<string>;
    descriptionFilterValue?: PresetValue<string>;
    tagsFilterValues?: PresetValue<string[]>;
    languagesFilterValues?: PresetValue<string[]>;
    sortColumn?: PresetValue<string>;
    sortDescending?: PresetValue<boolean>;
    minRatingFilterValue?: PresetValue<number>;
    maxRatingFilterValue?: PresetValue<number>;
    minActiveInstallsFilterValue?: PresetValue<number>;
    maxActiveInstallsFilterValue?: PresetValue<number>;
    minDownloadsFilterValue?: PresetValue<number>;
    maxDownloadsFilterValue?: PresetValue<number>;
    minTotalRatingsFilterValue?: PresetValue<number>;
    maxTotalRatingsFilterValue?: PresetValue<number>;
    minLastUpdatedFilterValue?: PresetValue<string>;
    maxLastUpdatedFilterValue?: PresetValue<string>;
    minAgeFilterValue?: PresetValue<number>;
    maxAgeFilterValue?: PresetValue<number>;
    minAddedFilterValue?: PresetValue<string>;
    maxAddedFilterValue?: PresetValue<string>;
};

type NichePreset = {
    id: string;
    title: string;
    description: string;
    pills: string[];
    filters: PresetFilters;
};

const resolvePresetValue = <T,>(value?: PresetValue<T>): T | undefined =>
    typeof value === 'function' ? (value as () => T)() : value;

export interface IPluginsSearchData {
    tags?: TagOption[];
    languages?: LanguageOption[];
    plugins?: IProcessedPluginData[];
    currentPage: number;
    totalPlugins: number;
    pluginsPerPage: number;
    totalPages: number;
    loadingTags: boolean;
    loadingLanguages: boolean;
    loadingPlugins: boolean;
}

PluginsList.getServerSideProps = async (context: GetServerSidePropsContext): Promise<IPluginsProps> => {
    const renderedAt = Date.now();
    const getQueryParam = (camelCase: string, underscore: string) => {
        const camelValue = context?.query?.[camelCase];
        const underscoreValue = context?.query?.[underscore];
        
        if (typeof camelValue !== 'undefined') {
            return camelValue.toString();
        }
        if (typeof underscoreValue !== 'undefined') {
            return underscoreValue.toString();
        }
        return undefined;
    };

    const getQueryParamNumber = (camelCase: string, underscore: string, defaultValue: number) => {
        const value = getQueryParam(camelCase, underscore);
        return value !== undefined ? parseInt(value) : defaultValue;
    };

    const requestedSortColumn = getQueryParam('sortColumn', 'sort_column') || DEFAULT_SORT_COLUMN;
    const requestedSortDescendingParam = getQueryParam('sortDescending', 'sort_descending');
    const requestedSortDescending = typeof requestedSortDescendingParam === 'undefined'
        ? DEFAULT_SORT_DESCENDING
        : requestedSortDescendingParam === 'true';
    const apiSort = getApiSort(requestedSortColumn, requestedSortDescending);

    const props: IPluginsProps = {
        searchFilterValue: getQueryParam('search', 'search') || '',
        nameFilterValue: getQueryParam('name', 'name') || '',
        descriptionFilterValue: getQueryParam('description', 'description') || '',
        languagesFilterValues: typeof context?.query?.languages !== 'undefined' ? context?.query?.languages.toString().split(',') : [],
        tagsFilterValues: typeof context?.query?.tags !== 'undefined' ? context?.query?.tags.toString().split(',') : [],
        sortColumn: requestedSortColumn,
        sortDescending: requestedSortDescending,
        page: getQueryParamNumber('page', 'page', 1),
        perPage: getQueryParamNumber('perPage', 'per_page', DEFAULT_RESULTS_PER_PAGE),
        minRatingFilterValue: getQueryParamNumber('minRating', 'min_rating', DEFAULT_MIN_RATING_FILTER_VALUE),
        maxRatingFilterValue: getQueryParamNumber('maxRating', 'max_rating', DEFAULT_MAX_RATING_FILTER_VALUE),
        minDownloadsFilterValue: getQueryParamNumber('minDownloads', 'min_downloads', DEFAULT_MIN_DOWNLOADS_FILTER_VALUE),
        maxDownloadsFilterValue: getQueryParamNumber('maxDownloads', 'max_downloads', DEFAULT_MAX_DOWNLOADS_FILTER_VALUE),
        minActiveInstallsFilterValue: getQueryParamNumber('minActiveInstalls', 'min_active_installs', DEFAULT_MIN_ACTIVE_INSTALLS_FILTER_VALUE),
        maxActiveInstallsFilterValue: getQueryParamNumber('maxActiveInstalls', 'max_active_installs', DEFAULT_MAX_ACTIVE_INSTALLS_FILTER_VALUE),
        minTotalRatingsFilterValue: getQueryParamNumber('minTotalRatings', 'min_total_ratings', DEFAULT_MIN_TOTAL_RATINGS_FILTER_VALUE),
        maxTotalRatingsFilterValue: getQueryParamNumber('maxTotalRatings', 'max_total_ratings', DEFAULT_MAX_TOTAL_RATINGS_FILTER_VALUE),
        minLastUpdatedFilterValue: getQueryParam('minLastUpdated', 'min_last_updated') || DEFAULT_MIN_LAST_UPDATED_FILTER_VALUE,
        maxLastUpdatedFilterValue: getQueryParam('maxLastUpdated', 'max_last_updated') || DEFAULT_MAX_LAST_UPDATED_FILTER_VALUE,
        minAgeFilterValue: getQueryParamNumber('minAge', 'min_age', DEFAULT_MIN_AGE_FILTER_VALUE),
        maxAgeFilterValue: getQueryParamNumber('maxAge', 'max_age', DEFAULT_MAX_AGE_FILTER_VALUE),
        minAddedFilterValue: getQueryParam('minAdded', 'min_added') || DEFAULT_MIN_ADDED_FILTER_VALUE,
        maxAddedFilterValue: getQueryParam('maxAdded', 'max_added') || DEFAULT_MAX_ADDED_FILTER_VALUE,
        languageOptionsJsons: [],
        tagOptionsJsons: [],
        renderedAt,
    };

    const protocolHeader = context.req?.headers['x-forwarded-proto'];
    const protocol = Array.isArray(protocolHeader) ? protocolHeader[0] : protocolHeader;
    const host = context.req?.headers['host'];
    const origin = host ? `${protocol ?? 'http'}://${host}` : undefined;

    const ssrPluginsData = await ssrUsePluginsSearch({
        search: props.searchFilterValue,
        name: props.nameFilterValue,
        description: props.descriptionFilterValue,
        languages: props.languagesFilterValues,
        tags: props.tagsFilterValues,
        sortColumn: apiSort.column,
        sortDescending: apiSort.descending,
        page: props.page,
        per_page: props.perPage,
        min_rating: props.minRatingFilterValue,
        max_rating: props.maxRatingFilterValue,
        min_active_installs: props.minActiveInstallsFilterValue,
        max_active_installs: props.maxActiveInstallsFilterValue,
        min_downloads: props.minDownloadsFilterValue,
        max_downloads: props.maxDownloadsFilterValue,
        min_total_ratings: props.minTotalRatingsFilterValue,
        max_total_ratings: props.maxTotalRatingsFilterValue,
        min_last_updated: props.minLastUpdatedFilterValue,
        max_last_updated: props.maxLastUpdatedFilterValue,
        min_age_days: props.minAgeFilterValue && props.minAgeFilterValue > DEFAULT_MIN_AGE_FILTER_VALUE ? yearsToDays(props.minAgeFilterValue) : undefined,
        max_age_days: props.maxAgeFilterValue && props.maxAgeFilterValue < DEFAULT_MAX_AGE_FILTER_VALUE ? yearsToDays(props.maxAgeFilterValue) : undefined,
        min_added: props.minAddedFilterValue,
        max_added: props.maxAddedFilterValue,
    }, origin, renderedAt);

    ssrPluginsData.plugins = ssrPluginsData.plugins?.map(plugin => {
        const strippedPlugin = {...plugin};
        strippedPlugin.banners = undefined;
        strippedPlugin.changelog = undefined;
        strippedPlugin.htmlStrippedChangelog = undefined;
        strippedPlugin.description = undefined;
        strippedPlugin.htmlStrippedDescription = strippedPlugin.htmlStrippedDescription?.slice(0, SSR_MAX_DESCRIPTION_LENGTH);
        strippedPlugin.faq = undefined;
        strippedPlugin.htmlStrippedFaq = undefined;
        strippedPlugin.installation = undefined;
        strippedPlugin.htmlStrippedInstallation = undefined;
        strippedPlugin.icons = strippedPlugin.icons?.slice(0, SSR_MAX_ICONS);
        strippedPlugin.reviews = [];
        strippedPlugin.screenshots = undefined;
        strippedPlugin.htmlStrippedScreenshots = undefined;
        return strippedPlugin;
    });

    props.stringifyPluginsSearchData = JSON.stringify(ssrPluginsData);

    return props;
}


function PluginsList(props: IPluginsProps) {

    const router = useRouter();
    const now = useStableNow(props.renderedAt);

    // State handlers related to data retrieval
    const [lastPluginsSearchData, setLastPluginsSearchData] = useState<IPluginsSearchData>();
    const [layout, setLayout] = useState<PluginsLayout>(DEFAULT_LAYOUT);
    const [visibleTableColumns, setVisibleTableColumns] = useState<TableColumnId[]>(DEFAULT_TABLE_COLUMNS);
    const [isColumnSelectorOpen, setIsColumnSelectorOpen] = useState<boolean>(false);
    const [isFiltersModalOpen, setIsFiltersModalOpen] = useState<boolean>(false);

    // State handlers related to data filtering
    const [searchFilterValue, setSearchFilterValue] = useState<string>(props.searchFilterValue || '');
    const [nameFilterValue, setNameFilterValue] = useState<string>(props.nameFilterValue || '');
    const [descriptionFilterValue, setDescriptionFilterValue] = useState<string>(props.descriptionFilterValue || '');
    const [tagsFilterValues, setTagsFilterValues] = useState<string[]>(props.tagsFilterValues || []);
    const [languagesFilterValues, setLanguagesFilterValues] = useState<string[]>(props.languagesFilterValues || []);
    const [minRatingFilterValue, setMinRatingFilterValue] = useState<number>(props.minRatingFilterValue || DEFAULT_MIN_RATING_FILTER_VALUE);
    const [maxRatingFilterValue, setMaxRatingFilterValue] = useState<number>(props.maxRatingFilterValue || DEFAULT_MAX_RATING_FILTER_VALUE);
    const [minActiveInstallsFilterValue, setMinActiveInstallsFilterValue] = useState<number>(props.minActiveInstallsFilterValue || DEFAULT_MIN_ACTIVE_INSTALLS_FILTER_VALUE);
    const [maxActiveInstallsFilterValue, setMaxActiveInstallsFilterValue] = useState<number>(props.maxActiveInstallsFilterValue || DEFAULT_MAX_ACTIVE_INSTALLS_FILTER_VALUE);
    const [minDownloadsFilterValue, setMinDownloadsFilterValue] = useState<number>(props.minDownloadsFilterValue || DEFAULT_MIN_DOWNLOADS_FILTER_VALUE);
    const [maxDownloadsFilterValue, setMaxDownloadsFilterValue] = useState<number>(props.maxDownloadsFilterValue || DEFAULT_MAX_DOWNLOADS_FILTER_VALUE);
    const [minTotalRatingsFilterValue, setMinTotalRatingsFilterValue] = useState<number>(props.minTotalRatingsFilterValue || DEFAULT_MIN_TOTAL_RATINGS_FILTER_VALUE);
    const [maxTotalRatingsFilterValue, setMaxTotalRatingsFilterValue] = useState<number>(props.maxTotalRatingsFilterValue || DEFAULT_MAX_TOTAL_RATINGS_FILTER_VALUE);
    const [minLastUpdatedFilterValue, setMinLastUpdatedFilterValue] = useState<string>(props.minLastUpdatedFilterValue || DEFAULT_MIN_LAST_UPDATED_FILTER_VALUE);
    const [maxLastUpdatedFilterValue, setMaxLastUpdatedFilterValue] = useState<string>(props.maxLastUpdatedFilterValue || DEFAULT_MAX_LAST_UPDATED_FILTER_VALUE);
    const [minAgeFilterValue, setMinAgeFilterValue] = useState<number>(props.minAgeFilterValue ?? DEFAULT_MIN_AGE_FILTER_VALUE);
    const [maxAgeFilterValue, setMaxAgeFilterValue] = useState<number>(props.maxAgeFilterValue ?? DEFAULT_MAX_AGE_FILTER_VALUE);
    const [minAddedFilterValue, setMinAddedFilterValue] = useState<string>(props.minAddedFilterValue || DEFAULT_MIN_ADDED_FILTER_VALUE);
    const [maxAddedFilterValue, setMaxAddedFilterValue] = useState<string>(props.maxAddedFilterValue || DEFAULT_MAX_ADDED_FILTER_VALUE);

    // State handlers related to data sorting
    const [sortColumn, setSortColumn] = useState<string>(props.sortColumn || DEFAULT_SORT_COLUMN);
    const [sortDescending, setSortDescending] = useState<boolean>(props.sortDescending ?? DEFAULT_SORT_DESCENDING);

    // State handlers related to pagination

    const [hasHydratedFromQuery, setHasHydratedFromQuery] = useState<boolean>(false);

    const parsedPluginsSearchDataProp: IPluginsSearchData = JSON.parse(props.stringifyPluginsSearchData ?? "{}")
    const hasInitialSearchData = (parsedPluginsSearchDataProp?.plugins?.length ?? 0) > 0
        || (parsedPluginsSearchDataProp?.totalPlugins ?? 0) > 0;

    const initialPlugins = parsedPluginsSearchDataProp?.plugins || [];
    const [pluginsData, setPluginsData] = useState<IProcessedPluginData[] | null>(initialPlugins || null);
    const [tablePluginsData, setTablePluginsData] = useState<IProcessedPluginData[]>(initialPlugins || []);
    const [tableQueryKey, setTableQueryKey] = useState<string>('');
    const [tagOptions, setTagOptions] = useState<TagOption[]>(parsedPluginsSearchDataProp?.tags?.map(tag => new TagOption(tag.slug, tag.label, tag.count)) || props.tagOptions || []);
    const [languageOptions, setLanguageOptions] = useState<LanguageOption[]>(parsedPluginsSearchDataProp?.languages?.map(language => new LanguageOption(language.slug, language.label, language.count)) || props.languageOptions || []);
    const [page, setPage] = useState<number>(parsedPluginsSearchDataProp?.currentPage || props.page || 1);
    const [totalPages, setTotalPages] = useState<number>(parsedPluginsSearchDataProp?.totalPages || 0);
    const [totalPlugins, setTotalPlugins] = useState<number>(parsedPluginsSearchDataProp?.totalPlugins || 0);
    const [perPage, setPerPage] = useState<number>(parsedPluginsSearchDataProp?.pluginsPerPage || props.perPage || DEFAULT_RESULTS_PER_PAGE);
    const [loading, setLoading] = useState<boolean>(false);
    const [enableQueryParams] = useState<boolean>(props.enableQueryParams ?? true);
    const [pluginsQuery, setPluginsQuery] = useState<IPluginsQuery | undefined>(() => {
        const apiSort = getApiSort(props.sortColumn || DEFAULT_SORT_COLUMN, props.sortDescending ?? DEFAULT_SORT_DESCENDING);
        const minAgeValue = props.minAgeFilterValue ?? DEFAULT_MIN_AGE_FILTER_VALUE;
        const maxAgeValue = props.maxAgeFilterValue ?? DEFAULT_MAX_AGE_FILTER_VALUE;

        return {
            page: props.page ?? 1,
            per_page: props.perPage ?? DEFAULT_RESULTS_PER_PAGE,
            search: props.searchFilterValue ?? '',
            name: props.nameFilterValue ?? '',
            description: props.descriptionFilterValue ?? '',
            sortColumn: apiSort.column,
            sortDescending: apiSort.descending,
            languages: props.languagesFilterValues ?? [],
            tags: props.tagsFilterValues ?? [],
            min_rating: props.minRatingFilterValue ?? DEFAULT_MIN_RATING_FILTER_VALUE,
            max_rating: props.maxRatingFilterValue ?? DEFAULT_MAX_RATING_FILTER_VALUE,
            min_active_installs: props.minActiveInstallsFilterValue ?? DEFAULT_MIN_ACTIVE_INSTALLS_FILTER_VALUE,
            max_active_installs: props.maxActiveInstallsFilterValue ?? DEFAULT_MAX_ACTIVE_INSTALLS_FILTER_VALUE,
            min_downloads: props.minDownloadsFilterValue ?? DEFAULT_MIN_DOWNLOADS_FILTER_VALUE,
            max_downloads: props.maxDownloadsFilterValue ?? DEFAULT_MAX_DOWNLOADS_FILTER_VALUE,
            min_total_ratings: props.minTotalRatingsFilterValue ?? DEFAULT_MIN_TOTAL_RATINGS_FILTER_VALUE,
            max_total_ratings: props.maxTotalRatingsFilterValue ?? DEFAULT_MAX_TOTAL_RATINGS_FILTER_VALUE,
            min_last_updated: props.minLastUpdatedFilterValue ?? DEFAULT_MIN_LAST_UPDATED_FILTER_VALUE,
            max_last_updated: props.maxLastUpdatedFilterValue ?? DEFAULT_MAX_LAST_UPDATED_FILTER_VALUE,
            min_age_days: minAgeValue > DEFAULT_MIN_AGE_FILTER_VALUE ? yearsToDays(minAgeValue) : undefined,
            max_age_days: maxAgeValue < DEFAULT_MAX_AGE_FILTER_VALUE ? yearsToDays(maxAgeValue) : undefined,
            min_added: props.minAddedFilterValue ?? DEFAULT_MIN_ADDED_FILTER_VALUE,
            max_added: props.maxAddedFilterValue ?? DEFAULT_MAX_ADDED_FILTER_VALUE,
        };
    });
    const pluginsSearchData = usePluginsSearch(pluginsQuery, hasInitialSearchData);
    const filtersPanelRef = useRef<HTMLDivElement | null>(null);
    const columnSelectorRef = useRef<HTMLDivElement | null>(null);
    const tableLoadMoreRef = useRef<HTMLDivElement | null>(null);
    const tableLoadingRef = useRef<boolean>(false);
    const prefetchedPluginIds = useRef<Set<string>>(new Set());
    const pendingScrollRestoreRef = useRef<number | null>(null);
    const pendingRestoreLayoutRef = useRef<PluginsLayout | null>(null);
    const shouldRestoreScrollRef = useRef<boolean>(false);
    const hasCheckedRestoreRef = useRef<boolean>(false);

    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }
        const storedColumns = window.localStorage.getItem('pluginsTableColumns');
        if (!storedColumns) {
            return;
        }
        try {
            const parsed = JSON.parse(storedColumns);
            if (Array.isArray(parsed)) {
                const sanitized = parsed.filter((column: any): column is TableColumnId => ALL_TABLE_COLUMNS.includes(column));
                const nextColumns = sanitized.length > 0 ? sanitized : DEFAULT_TABLE_COLUMNS;
                if (sanitized.length > 0) {
                    setVisibleTableColumns(Array.from(new Set([...REQUIRED_TABLE_COLUMNS, ...nextColumns])));
                }
            } else {
                setVisibleTableColumns(DEFAULT_TABLE_COLUMNS);
            }
        } catch {
            // Ignore invalid cache
        }
    }, []);

    useEffect(() => {
        if (typeof window === 'undefined') {
            return;
        }
        window.localStorage.setItem('pluginsTableColumns', JSON.stringify(visibleTableColumns));
    }, [visibleTableColumns]);

    useEffect(() => {
        if (!isColumnSelectorOpen) {
            return;
        }
        const handleClickOutside = (event: MouseEvent) => {
            if (columnSelectorRef.current && !columnSelectorRef.current.contains(event.target as Node)) {
                setIsColumnSelectorOpen(false);
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => {
            document.removeEventListener('mousedown', handleClickOutside);
        };
    }, [isColumnSelectorOpen]);

    useEffect(() => {
        if (layout !== 'table') {
            setIsColumnSelectorOpen(false);
        }
    }, [layout]);

    const getDateNDaysAgo = useCallback((days: number): string => {
        if (!now) return '';
        const date = new Date(now);
        date.setDate(date.getDate() - days);
        return date.toISOString().split('T')[0];
    }, [now]);

    const resetFilters = useCallback(() => {
        setSearchFilterValue('');
        setNameFilterValue('');
        setDescriptionFilterValue('');
        setLanguagesFilterValues([]);
        setTagsFilterValues([]);
        setSortColumn('');
        setSortDescending(false);
        setMinRatingFilterValue(0);
        setPage(1);
        setMaxRatingFilterValue(5);
        setMinActiveInstallsFilterValue(DEFAULT_MIN_ACTIVE_INSTALLS_FILTER_VALUE);
        setMaxActiveInstallsFilterValue(DEFAULT_MAX_ACTIVE_INSTALLS_FILTER_VALUE);
        setMinDownloadsFilterValue(DEFAULT_MIN_DOWNLOADS_FILTER_VALUE);
        setMaxDownloadsFilterValue(DEFAULT_MAX_DOWNLOADS_FILTER_VALUE);
        setMinTotalRatingsFilterValue(DEFAULT_MIN_TOTAL_RATINGS_FILTER_VALUE);
        setMaxTotalRatingsFilterValue(DEFAULT_MAX_TOTAL_RATINGS_FILTER_VALUE);
        setMinLastUpdatedFilterValue(DEFAULT_MIN_LAST_UPDATED_FILTER_VALUE);
        setMaxLastUpdatedFilterValue(DEFAULT_MAX_LAST_UPDATED_FILTER_VALUE);
        setMinAgeFilterValue(DEFAULT_MIN_AGE_FILTER_VALUE);
        setMaxAgeFilterValue(DEFAULT_MAX_AGE_FILTER_VALUE);
        setMinAddedFilterValue(DEFAULT_MIN_ADDED_FILTER_VALUE);
        setMaxAddedFilterValue(DEFAULT_MAX_ADDED_FILTER_VALUE);
    }, []);

    const countUsedFilters = useCallback(() => {
        let count = 0;
        if (searchFilterValue) count++;
        if (nameFilterValue) count++;
        if (descriptionFilterValue) count++;
        if (languagesFilterValues.length > 0) count++;
        if (tagsFilterValues.length > 0) count++;
        if (minRatingFilterValue > DEFAULT_MIN_RATING_FILTER_VALUE) count++;
        if (maxRatingFilterValue < DEFAULT_MAX_RATING_FILTER_VALUE) count++;
        if (minActiveInstallsFilterValue > DEFAULT_MIN_ACTIVE_INSTALLS_FILTER_VALUE) count++;
        if (maxActiveInstallsFilterValue < DEFAULT_MAX_ACTIVE_INSTALLS_FILTER_VALUE) count++;
        if (minDownloadsFilterValue > DEFAULT_MIN_DOWNLOADS_FILTER_VALUE) count++;
        if (maxDownloadsFilterValue < DEFAULT_MAX_DOWNLOADS_FILTER_VALUE) count++;
        if (minTotalRatingsFilterValue > DEFAULT_MIN_TOTAL_RATINGS_FILTER_VALUE) count++;
        if (maxTotalRatingsFilterValue < DEFAULT_MAX_TOTAL_RATINGS_FILTER_VALUE) count++;
        if (minLastUpdatedFilterValue !== DEFAULT_MIN_LAST_UPDATED_FILTER_VALUE) count++;
        if (maxLastUpdatedFilterValue !== DEFAULT_MAX_LAST_UPDATED_FILTER_VALUE) count++;
        if (minAgeFilterValue > DEFAULT_MIN_AGE_FILTER_VALUE) count++;
        if (maxAgeFilterValue < DEFAULT_MAX_AGE_FILTER_VALUE) count++;
        if (minAddedFilterValue !== DEFAULT_MIN_ADDED_FILTER_VALUE) count++;
        if (maxAddedFilterValue !== DEFAULT_MAX_ADDED_FILTER_VALUE) count++;
        return count;
    }, [searchFilterValue, nameFilterValue, descriptionFilterValue, languagesFilterValues, tagsFilterValues,
        minRatingFilterValue, maxRatingFilterValue, minActiveInstallsFilterValue, maxActiveInstallsFilterValue,
        minDownloadsFilterValue, maxDownloadsFilterValue, minTotalRatingsFilterValue, maxTotalRatingsFilterValue,
        minLastUpdatedFilterValue, maxLastUpdatedFilterValue, minAgeFilterValue, maxAgeFilterValue, minAddedFilterValue, maxAddedFilterValue]);

    const nichePresets: NichePreset[] = useMemo(() => [
        {
            id: 'hidden-gems',
            title: 'Hidden gems',
            description: 'Well-rated plugins with low installs and enough reviews to trust the signal.',
            pills: ['≥4.6 rating', '≤20k installs', '≥20 reviews', 'Sort: rating desc'],
            filters: {
                minRatingFilterValue: 4.6,
                maxActiveInstallsFilterValue: 20000,
                minTotalRatingsFilterValue: 20,
                sortColumn: 'rating',
                sortDescending: true,
            },
        },
        {
            id: 'recently-updated',
            title: 'Fresh & niche',
            description: 'Newly published projects that are still early in installs.',
            pills: ['Published in last 6 months', '≤15k installs', '≥4.2 rating', 'Sort: newest published'],
            filters: {
                maxActiveInstallsFilterValue: 15000,
                minRatingFilterValue: 4.2,
                minAddedFilterValue: () => getDateNDaysAgo(180),
                sortColumn: 'age',
                sortDescending: false,
            },
        },
        {
            id: 'room-to-disrupt',
            title: 'Room to disrupt',
            description: 'High-install plugins with weak ratings to find gaps you can outperform.',
            pills: ['≥50k installs', '≤3.6 rating', '≥30 reviews', 'Sort: rating asc'],
            filters: {
                minActiveInstallsFilterValue: 50000,
                maxRatingFilterValue: 3.6,
                minTotalRatingsFilterValue: 30,
                sortColumn: 'rating',
                sortDescending: false,
            },
        },
    ], [getDateNDaysAgo]);

    const getCurrentPluginsQuery = useCallback((): IPluginsQuery => {
        const apiSort = getApiSort(sortColumn, sortDescending);
        return {
            page: page,
            per_page: perPage,
            search: searchFilterValue,
            name: nameFilterValue,
            description: descriptionFilterValue,
            sortColumn: apiSort.column,
            sortDescending: apiSort.descending,
            languages: languagesFilterValues,
            tags: tagsFilterValues,
            min_rating: minRatingFilterValue,
            max_rating: maxRatingFilterValue,
            min_active_installs: minActiveInstallsFilterValue,
            max_active_installs: maxActiveInstallsFilterValue,
            min_downloads: minDownloadsFilterValue,
            max_downloads: maxDownloadsFilterValue,
            min_total_ratings: minTotalRatingsFilterValue,
            max_total_ratings: maxTotalRatingsFilterValue,
            min_last_updated: minLastUpdatedFilterValue,
            max_last_updated: maxLastUpdatedFilterValue,
            min_age_days: minAgeFilterValue > DEFAULT_MIN_AGE_FILTER_VALUE ? yearsToDays(minAgeFilterValue) : undefined,
            max_age_days: maxAgeFilterValue < DEFAULT_MAX_AGE_FILTER_VALUE ? yearsToDays(maxAgeFilterValue) : undefined,
            min_added: minAddedFilterValue,
            max_added: maxAddedFilterValue,
        };
    }, [page, perPage, searchFilterValue, nameFilterValue, descriptionFilterValue, sortColumn, sortDescending,
        languagesFilterValues, tagsFilterValues, minRatingFilterValue, maxRatingFilterValue,
        minActiveInstallsFilterValue, maxActiveInstallsFilterValue, minDownloadsFilterValue,
        maxDownloadsFilterValue, minTotalRatingsFilterValue, maxTotalRatingsFilterValue,
        minLastUpdatedFilterValue, maxLastUpdatedFilterValue, minAddedFilterValue, maxAddedFilterValue, minAgeFilterValue, maxAgeFilterValue]);

    const buildParsedUrlQueryInput = useCallback((): ParsedUrlQueryInput => {
        const query: ParsedUrlQueryInput = {};

        // Only add non-default values
        if (page !== 1) query.page = page.toString();
        if (perPage !== DEFAULT_RESULTS_PER_PAGE) query.per_page = perPage.toString();
        if (searchFilterValue) query.search = searchFilterValue;
        if (nameFilterValue) query.name = nameFilterValue;
        if (descriptionFilterValue) query.description = descriptionFilterValue;
        if (sortColumn && sortColumn !== DEFAULT_SORT_COLUMN) query.sortColumn = sortColumn;
        if (sortDescending !== DEFAULT_SORT_DESCENDING) query.sortDescending = sortDescending.toString();
        if (languagesFilterValues.length > 0) query.languages = languagesFilterValues;
        if (tagsFilterValues.length > 0) query.tags = tagsFilterValues;
        
        if (minRatingFilterValue !== DEFAULT_MIN_RATING_FILTER_VALUE) 
            query.min_rating = minRatingFilterValue.toString();
        if (maxRatingFilterValue !== DEFAULT_MAX_RATING_FILTER_VALUE) 
            query.max_rating = maxRatingFilterValue.toString();
            
        if (minActiveInstallsFilterValue !== DEFAULT_MIN_ACTIVE_INSTALLS_FILTER_VALUE) 
            query.min_active_installs = minActiveInstallsFilterValue.toString();
        if (maxActiveInstallsFilterValue !== DEFAULT_MAX_ACTIVE_INSTALLS_FILTER_VALUE) 
            query.max_active_installs = maxActiveInstallsFilterValue.toString();
            
        if (minDownloadsFilterValue !== DEFAULT_MIN_DOWNLOADS_FILTER_VALUE) 
            query.min_downloads = minDownloadsFilterValue.toString();
        if (maxDownloadsFilterValue !== DEFAULT_MAX_DOWNLOADS_FILTER_VALUE) 
            query.max_downloads = maxDownloadsFilterValue.toString();
            
        if (minTotalRatingsFilterValue !== DEFAULT_MIN_TOTAL_RATINGS_FILTER_VALUE) 
            query.min_total_ratings = minTotalRatingsFilterValue.toString();
        if (maxTotalRatingsFilterValue !== DEFAULT_MAX_TOTAL_RATINGS_FILTER_VALUE) 
            query.max_total_ratings = maxTotalRatingsFilterValue.toString();

        // For date fields, include them in URL if they have a value or if they're in the URL
        if (minLastUpdatedFilterValue !== DEFAULT_MIN_LAST_UPDATED_FILTER_VALUE) query.min_last_updated = minLastUpdatedFilterValue;
        if (maxLastUpdatedFilterValue !== DEFAULT_MAX_LAST_UPDATED_FILTER_VALUE) query.max_last_updated = maxLastUpdatedFilterValue;
        if (minAgeFilterValue !== DEFAULT_MIN_AGE_FILTER_VALUE) query.min_age = minAgeFilterValue.toString();
        if (maxAgeFilterValue !== DEFAULT_MAX_AGE_FILTER_VALUE) query.max_age = maxAgeFilterValue.toString();
        if (minAddedFilterValue !== DEFAULT_MIN_ADDED_FILTER_VALUE) query.min_added = minAddedFilterValue;
        if (maxAddedFilterValue !== DEFAULT_MAX_ADDED_FILTER_VALUE) query.max_added = maxAddedFilterValue;

        return query;
    }, [page, perPage, searchFilterValue, nameFilterValue, descriptionFilterValue, sortColumn, sortDescending,
        languagesFilterValues, tagsFilterValues, minRatingFilterValue, maxRatingFilterValue,
        minActiveInstallsFilterValue, maxActiveInstallsFilterValue, minDownloadsFilterValue,
        maxDownloadsFilterValue, minTotalRatingsFilterValue, maxTotalRatingsFilterValue, minLastUpdatedFilterValue,
        maxLastUpdatedFilterValue, minAddedFilterValue, maxAddedFilterValue, minAgeFilterValue, maxAgeFilterValue]);

    const processPluginsSearchData = useCallback((data: IPluginsSearchData) => {
        if (data.tags) {
            setTagOptions([...data.tags, ...tagOptions.filter((tagOption) => 
                tagsFilterValues.includes(tagOption.getSlug()) && 
                !data.tags?.filter((tag) => tag.getSlug() === tagOption.getSlug()).length)]);
        }

        if (data.languages) {
            setLanguageOptions([...data.languages, ...languageOptions.filter((languageOption) => 
                languagesFilterValues.includes(languageOption.getSlug()) && 
                !data.languages?.filter((language) => language.getSlug() === languageOption.getSlug()).length)]);
        }

        if (data.plugins) {
            const shouldScrollToTop = layout !== 'table' || data.currentPage === 1;
            if (shouldScrollToTop && !shouldRestoreScrollRef.current &&
                (data.plugins.length !== pluginsData?.length ||
                    data.plugins.some((plugin, index) => plugin.pluginid !== pluginsData[index]?.pluginid))) {
                // scroll main content to top (avoid during table infinite scroll)
                if (typeof document !== 'undefined') {
                    const main = document.getElementsByTagName('main')[0]
                    if (main) {
                        main.scrollTo({
                            top: 0,
                            behavior: 'smooth'
                        })
                    }
                }
            }

            if (layout === 'table') {
                setTablePluginsData((prev) => {
                    const base = data.currentPage === 1 ? [] : (prev || []);
                    const existingIds = new Set(base.map((plugin) => plugin.pluginid));
                    const merged = [...base];
                    data.plugins?.forEach((plugin) => {
                        if (!existingIds.has(plugin.pluginid)) {
                            merged.push(plugin);
                        }
                    });
                    return merged;
                });
            }

            setPluginsData(data.plugins);
            setTotalPages(data.totalPages || 0);
            setTotalPlugins(data.totalPlugins || 0);
        }

        setLoading(data.loadingPlugins ?? false);
        setLastPluginsSearchData(data);
    }, [tagOptions, tagsFilterValues, languageOptions, languagesFilterValues, pluginsData, layout]);

    useEffect(() => {
        if (!router.isReady || typeof window === 'undefined') {
            return;
        }
        if (hasCheckedRestoreRef.current) {
            return;
        }
        hasCheckedRestoreRef.current = true;
        const stored = readPluginsListState();
        if (!stored) {
            return;
        }
        if (normalizePluginsListUrl(stored.url) !== normalizePluginsListUrl(router.asPath)) {
            return;
        }
        pendingScrollRestoreRef.current = stored.scrollTop;
        shouldRestoreScrollRef.current = true;

        if (stored.layout && isValidLayout(stored.layout)) {
            pendingRestoreLayoutRef.current = stored.layout;
            if (stored.layout !== layout) {
                setLayout(stored.layout);
            }
        }
    }, [layout, router.asPath, router.isReady]);

    useEffect(() => {
        if (!isEqual(lastPluginsSearchData, pluginsSearchData)) {
            processPluginsSearchData(pluginsSearchData);
        }
    }, [pluginsSearchData, lastPluginsSearchData, processPluginsSearchData]);

    useEffect(() => {
        if (!shouldRestoreScrollRef.current) {
            return;
        }
        if (pendingScrollRestoreRef.current === null) {
            return;
        }
        if (pluginsData === null) {
            return;
        }
        if (pendingRestoreLayoutRef.current && pendingRestoreLayoutRef.current !== layout) {
            return;
        }
        const scrollTop = pendingScrollRestoreRef.current;
        const applyScroll = () => {
            const container = getPluginsListScrollContainer();
            if (container) {
                container.scrollTo({top: scrollTop, left: 0, behavior: 'auto'});
            } else if (typeof window !== 'undefined') {
                window.scrollTo({top: scrollTop, left: 0, behavior: 'auto'});
            }
        };

        if (typeof window !== 'undefined') {
            window.requestAnimationFrame(() => {
                applyScroll();
                window.requestAnimationFrame(() => applyScroll());
            });
        } else {
            applyScroll();
        }

        shouldRestoreScrollRef.current = false;
        pendingScrollRestoreRef.current = null;
        pendingRestoreLayoutRef.current = null;
        clearPluginsListState();
    }, [layout, pluginsData]);

    useEffect(() => {
        if (!router.isReady || hasHydratedFromQuery) {
            return;
        }

        const getQueryParam = (camelCase: string, underscore: string) => {
            const camelValue = router.query?.[camelCase];
            const underscoreValue = router.query?.[underscore];

            if (typeof camelValue !== 'undefined') {
                return camelValue.toString();
            }
            if (typeof underscoreValue !== 'undefined') {
                return underscoreValue.toString();
            }
            return undefined;
        };

        const getQueryParamNumber = (camelCase: string, underscore: string, defaultValue: number) => {
            const value = getQueryParam(camelCase, underscore);
            return value !== undefined && !isNaN(parseInt(value, 10)) ? parseInt(value, 10) : defaultValue;
        };

        setSearchFilterValue(getQueryParam('search', 'search') || '');
        setNameFilterValue(getQueryParam('name', 'name') || '');
        setDescriptionFilterValue(getQueryParam('description', 'description') || '');
        setLanguagesFilterValues(typeof router.query.languages !== 'undefined' ? router.query.languages.toString().split(',') : []);
        setTagsFilterValues(typeof router.query.tags !== 'undefined' ? router.query.tags.toString().split(',') : []);
        setSortColumn(getQueryParam('sortColumn', 'sort_column') || DEFAULT_SORT_COLUMN);
        const sortDescendingParam = getQueryParam('sortDescending', 'sort_descending');
        setSortDescending(typeof sortDescendingParam === 'undefined'
            ? DEFAULT_SORT_DESCENDING
            : sortDescendingParam === 'true');
        setPage(getQueryParamNumber('page', 'page', props.page || 1));
        setPerPage(getQueryParamNumber('perPage', 'per_page', props.perPage || DEFAULT_RESULTS_PER_PAGE));
        setMinRatingFilterValue(getQueryParamNumber('minRating', 'min_rating', DEFAULT_MIN_RATING_FILTER_VALUE));
        setMaxRatingFilterValue(getQueryParamNumber('maxRating', 'max_rating', DEFAULT_MAX_RATING_FILTER_VALUE));
        setMinDownloadsFilterValue(getQueryParamNumber('minDownloads', 'min_downloads', DEFAULT_MIN_DOWNLOADS_FILTER_VALUE));
        setMaxDownloadsFilterValue(getQueryParamNumber('maxDownloads', 'max_downloads', DEFAULT_MAX_DOWNLOADS_FILTER_VALUE));
        setMinActiveInstallsFilterValue(getQueryParamNumber('minActiveInstalls', 'min_active_installs', DEFAULT_MIN_ACTIVE_INSTALLS_FILTER_VALUE));
        setMaxActiveInstallsFilterValue(getQueryParamNumber('maxActiveInstalls', 'max_active_installs', DEFAULT_MAX_ACTIVE_INSTALLS_FILTER_VALUE));
        setMinTotalRatingsFilterValue(getQueryParamNumber('minTotalRatings', 'min_total_ratings', DEFAULT_MIN_TOTAL_RATINGS_FILTER_VALUE));
        setMaxTotalRatingsFilterValue(getQueryParamNumber('maxTotalRatings', 'max_total_ratings', DEFAULT_MAX_TOTAL_RATINGS_FILTER_VALUE));
        setMinLastUpdatedFilterValue(getQueryParam('minLastUpdated', 'min_last_updated') || DEFAULT_MIN_LAST_UPDATED_FILTER_VALUE);
        setMaxLastUpdatedFilterValue(getQueryParam('maxLastUpdated', 'max_last_updated') || DEFAULT_MAX_LAST_UPDATED_FILTER_VALUE);
        setMinAgeFilterValue(getQueryParamNumber('minAge', 'min_age', DEFAULT_MIN_AGE_FILTER_VALUE));
        setMaxAgeFilterValue(getQueryParamNumber('maxAge', 'max_age', DEFAULT_MAX_AGE_FILTER_VALUE));
        setMinAddedFilterValue(getQueryParam('minAdded', 'min_added') || DEFAULT_MIN_ADDED_FILTER_VALUE);
        setMaxAddedFilterValue(getQueryParam('maxAdded', 'max_added') || DEFAULT_MAX_ADDED_FILTER_VALUE);

        setHasHydratedFromQuery(true);
    }, [router.isReady, router.query, hasHydratedFromQuery, props.page, props.perPage]);

    useEffect(() => {
        if (!router.isReady || !hasHydratedFromQuery) {
            return;
        }

        if (!isEqual(pluginsQuery, getCurrentPluginsQuery())) {
            if (enableQueryParams) {
                router.replace({
                    query: buildParsedUrlQueryInput() as ParsedUrlQueryInput
                }, undefined, {shallow: true});
            }

            setPluginsQuery(getCurrentPluginsQuery());
        }
    }, [pluginsQuery, getCurrentPluginsQuery, enableQueryParams, router, buildParsedUrlQueryInput, hasHydratedFromQuery, router.isReady]);

    const handlePageChange = (e: number) => {
        setPage(e);
    }

    const buildPluginsPageHref = useCallback((targetPage: number) => {
        const query = buildParsedUrlQueryInput();
        if (targetPage === 1) {
            delete query.page;
        } else {
            query.page = targetPage.toString();
        }
        const params = new URLSearchParams();
        Object.entries(query).forEach(([key, value]) => {
            if (value === undefined || value === null || value === '') {
                return;
            }
            if (Array.isArray(value)) {
                params.set(key, value.join(','));
                return;
            }
            params.set(key, String(value));
        });
        const suffix = params.toString();
        return suffix ? `/plugins?${suffix}` : '/plugins';
    }, [buildParsedUrlQueryInput]);

    const scrollToFilters = useCallback(() => {
        if (typeof window === 'undefined') {
            return;
        }
        if (window.matchMedia('(max-width: 767px)').matches) {
            setIsFiltersModalOpen(true);
            return;
        }
        filtersPanelRef.current?.scrollIntoView({behavior: 'smooth', block: 'start'});
    }, [setIsFiltersModalOpen]);

    const applyPreset = (preset: NichePreset) => {
        resetFilters();
        setPage(1);

        const filters = preset.filters;

        const searchValue = resolvePresetValue(filters.searchFilterValue);
        if (typeof searchValue === 'string') {
            setSearchFilterValue(searchValue);
        }

        const nameValue = resolvePresetValue(filters.nameFilterValue);
        if (typeof nameValue === 'string') {
            setNameFilterValue(nameValue);
        }

        const descriptionValue = resolvePresetValue(filters.descriptionFilterValue);
        if (typeof descriptionValue === 'string') {
            setDescriptionFilterValue(descriptionValue);
        }

        const tagsValue = resolvePresetValue(filters.tagsFilterValues);
        if (tagsValue) {
            setTagsFilterValues(tagsValue);
        }

        const languagesValue = resolvePresetValue(filters.languagesFilterValues);
        if (languagesValue) {
            setLanguagesFilterValues(languagesValue);
        }

        const minRatingValue = resolvePresetValue(filters.minRatingFilterValue);
        if (typeof minRatingValue === 'number') {
            setMinRatingFilterValue(minRatingValue);
        }

        const maxRatingValue = resolvePresetValue(filters.maxRatingFilterValue);
        if (typeof maxRatingValue === 'number') {
            setMaxRatingFilterValue(maxRatingValue);
        }

        const minActiveInstallsValue = resolvePresetValue(filters.minActiveInstallsFilterValue);
        if (typeof minActiveInstallsValue === 'number') {
            setMinActiveInstallsFilterValue(minActiveInstallsValue);
        }

        const maxActiveInstallsValue = resolvePresetValue(filters.maxActiveInstallsFilterValue);
        if (typeof maxActiveInstallsValue === 'number') {
            setMaxActiveInstallsFilterValue(maxActiveInstallsValue);
        }

        const minDownloadsValue = resolvePresetValue(filters.minDownloadsFilterValue);
        if (typeof minDownloadsValue === 'number') {
            setMinDownloadsFilterValue(minDownloadsValue);
        }

        const maxDownloadsValue = resolvePresetValue(filters.maxDownloadsFilterValue);
        if (typeof maxDownloadsValue === 'number') {
            setMaxDownloadsFilterValue(maxDownloadsValue);
        }

        const minTotalRatingsValue = resolvePresetValue(filters.minTotalRatingsFilterValue);
        if (typeof minTotalRatingsValue === 'number') {
            setMinTotalRatingsFilterValue(minTotalRatingsValue);
        }

        const maxTotalRatingsValue = resolvePresetValue(filters.maxTotalRatingsFilterValue);
        if (typeof maxTotalRatingsValue === 'number') {
            setMaxTotalRatingsFilterValue(maxTotalRatingsValue);
        }

        const minLastUpdatedValue = resolvePresetValue(filters.minLastUpdatedFilterValue);
        if (typeof minLastUpdatedValue === 'string') {
            setMinLastUpdatedFilterValue(minLastUpdatedValue);
        }

        const maxLastUpdatedValue = resolvePresetValue(filters.maxLastUpdatedFilterValue);
        if (typeof maxLastUpdatedValue === 'string') {
            setMaxLastUpdatedFilterValue(maxLastUpdatedValue);
        }

        const minAgeValue = resolvePresetValue(filters.minAgeFilterValue);
        if (typeof minAgeValue === 'number') {
            setMinAgeFilterValue(minAgeValue);
        }

        const maxAgeValue = resolvePresetValue(filters.maxAgeFilterValue);
        if (typeof maxAgeValue === 'number') {
            setMaxAgeFilterValue(maxAgeValue);
        }

        const minAddedValue = resolvePresetValue(filters.minAddedFilterValue);
        if (typeof minAddedValue === 'string') {
            setMinAddedFilterValue(minAddedValue);
        }

        const maxAddedValue = resolvePresetValue(filters.maxAddedFilterValue);
        if (typeof maxAddedValue === 'string') {
            setMaxAddedFilterValue(maxAddedValue);
        }

        const sortColumnValue = resolvePresetValue(filters.sortColumn);
        if (typeof sortColumnValue === 'string') {
            setSortColumn(sortColumnValue);
        }

        const sortDescendingValue = resolvePresetValue(filters.sortDescending);
        if (typeof sortDescendingValue === 'boolean') {
            setSortDescending(sortDescendingValue);
        }

        scrollToFilters();
    };

    const buildTableQueryKey = useCallback(() => {
        const apiSort = getApiSort(sortColumn, sortDescending);
        return JSON.stringify({
            searchFilterValue,
            nameFilterValue,
            descriptionFilterValue,
            languagesFilterValues,
            tagsFilterValues,
            sortColumn: apiSort.column,
            sortDescending: apiSort.descending,
            perPage,
            minRatingFilterValue,
            maxRatingFilterValue,
            minDownloadsFilterValue,
            maxDownloadsFilterValue,
            minActiveInstallsFilterValue,
            maxActiveInstallsFilterValue,
            minTotalRatingsFilterValue,
            maxTotalRatingsFilterValue,
            minLastUpdatedFilterValue,
            maxLastUpdatedFilterValue,
            minAgeFilterValue,
            maxAgeFilterValue,
            minAddedFilterValue,
            maxAddedFilterValue,
        });
    }, [descriptionFilterValue, languagesFilterValues, maxActiveInstallsFilterValue, maxDownloadsFilterValue, maxLastUpdatedFilterValue, maxRatingFilterValue, maxTotalRatingsFilterValue, minActiveInstallsFilterValue, minDownloadsFilterValue, minLastUpdatedFilterValue, minRatingFilterValue, minTotalRatingsFilterValue, nameFilterValue, perPage, searchFilterValue, sortColumn, sortDescending, tagsFilterValues, minAddedFilterValue, maxAddedFilterValue, minAgeFilterValue, maxAgeFilterValue]);

    const prefetchPlugin = useCallback((pluginId: string) => {
        if (!pluginId || prefetchedPluginIds.current.has(pluginId)) {
            return;
        }
        prefetchedPluginIds.current.add(pluginId);
        // Prefetch intentionally disabled to force full document requests on navigation.
    }, []);

    const captureListState = useCallback(() => {
        if (typeof window === 'undefined') {
            return;
        }
        const scrollContainer = getPluginsListScrollContainer();
        const scrollTop = scrollContainer ? scrollContainer.scrollTop : window.scrollY;
        writePluginsListState({
            url: normalizePluginsListUrl(router.asPath),
            scrollTop,
            timestamp: Date.now(),
            layout,
        });
    }, [layout, router.asPath]);

    const handlePluginLinkClick = useCallback(() => {
        captureListState();
    }, [captureListState]);


    const calculateAgeDays = useCallback((added?: string | Date) => {
        if (!added || !now) return null;
        const ageMs = now - new Date(added).getTime();
        if (!isFinite(ageMs) || ageMs < 0) return null;
        return ageMs / (1000 * 60 * 60 * 24);
    }, [now]);

    const formatNumber = useCallback((value: number | null | undefined, maximumFractionDigits = 0) => {
        if (value === null || value === undefined || !isFinite(value)) return '—';
        return formatNumberLocale(value, {maximumFractionDigits});
    }, []);

    const safeDivide = useCallback((numerator: number | null | undefined, denominator: number | null | undefined) => {
        if (numerator === null || numerator === undefined) return null;
        if (!denominator) return null;
        return numerator / denominator;
    }, []);

    const getAgeDays = useCallback((plugin: IProcessedPluginData) => {
        if (plugin.age_days !== undefined && plugin.age_days !== null) {
            return plugin.age_days;
        }
        return calculateAgeDays(plugin.added);
    }, [calculateAgeDays]);

    const formatAgeLabel = useCallback((ageInDays: number | null) => {
        if (ageInDays === null) return '—';
        if (ageInDays >= 365) {
            return `${(ageInDays / 365).toFixed(1)}y`;
        }
        if (ageInDays >= 30) {
            return `${(ageInDays / 30).toFixed(1)}mo`;
        }
        return `${Math.round(ageInDays)}d`;
    }, []);

    const getInstallsPerRating = useCallback((plugin: IProcessedPluginData) => plugin.installs_per_rating ?? safeDivide(plugin.active_installs, plugin.num_ratings), [safeDivide]);
    const getDownloadsPerRating = useCallback((plugin: IProcessedPluginData) => plugin.downloads_per_rating ?? safeDivide(plugin.downloaded, plugin.num_ratings), [safeDivide]);
    const getInstallsPerDay = useCallback((plugin: IProcessedPluginData) => plugin.installs_per_day ?? safeDivide(plugin.active_installs, getAgeDays(plugin)), [getAgeDays, safeDivide]);
    const getDownloadsPerDay = useCallback((plugin: IProcessedPluginData) => plugin.downloads_per_day ?? safeDivide(plugin.downloaded, getAgeDays(plugin)), [getAgeDays, safeDivide]);
    const getReviewsPerDay = useCallback((plugin: IProcessedPluginData) => plugin.reviews_per_day ?? safeDivide(plugin.num_ratings, getAgeDays(plugin)), [getAgeDays, safeDivide]);
    const getActivationRate = useCallback((plugin: IProcessedPluginData) => plugin.activation_rate ?? safeDivide(plugin.active_installs, plugin.downloaded), [safeDivide]);

    const getTagSortValue = useCallback((plugin: IProcessedPluginData) => {
        if (plugin.tags_count !== undefined && plugin.tags_count !== null) return plugin.tags_count;
        if (!plugin.tags?.length) return 0;
        const totalCount = plugin.tags.reduce((sum, tag) => sum + (tag.count ?? 0), 0);
        return totalCount || plugin.tags.length;
    }, []);

    const getLanguageSortValue = useCallback((plugin: IProcessedPluginData) => {
        if (plugin.languages_count !== undefined && plugin.languages_count !== null) return plugin.languages_count;
        if (!plugin.languages?.length) return 0;
        const totalCount = plugin.languages.reduce((sum, language) => sum + (language.count ?? 0), 0);
        return totalCount || plugin.languages.length;
    }, []);

    const tableColumns = useMemo<TableColumnDefinition[]>(() => {
        const formatDate = (value?: string | Date) => value ? formatDateLocale(value) : '';

        return [
            {
                id: 'name',
                label: 'Plugin',
                sortable: true,
                defaultDescending: false,
                sortType: 'string',
                headerClassName: "sticky left-0 z-[2] bg-gray-100 dark:bg-gray-800",
                contentClassName: "md:w-[18rem]",
                cellClassName: "min-w-[240px] sticky left-0 z-[1] bg-white dark:bg-gray-900",
                isFilterable: true,
                isFiltered: !!(searchFilterValue || nameFilterValue),
                getSortValue: (plugin) => plugin.name?.toLowerCase?.() ?? plugin.name,
                renderCell: (plugin) => (
                    <div className="flex items-center gap-3">
                        <div
                            className={cx("relative flex-shrink-0", "w-10 h-10")}
                            title={plugin.name}
                        >
                            {plugin.icons?.length > 0 && <Image
                                src={plugin.icons?.[0]?.url}
                                alt={plugin.name}
                                fill
                                className="object-contain"
                                unoptimized={true}/> || <FaPlug
                                className={cx("text-gray-400 dark:text-gray-600", "bg-gray-100 dark:bg-gray-800", "rounded-md", "w-10 h-10")}/>}
                        </div>
                        <div className="flex flex-col">
                            <Link
                                href={`/plugins/${plugin.pluginid}`}
                                onClick={handlePluginLinkClick}
                                className="text-sm font-semibold text-gray-800 dark:text-gray-200 hover:underline"
                            >
                                {plugin.name}
                            </Link>
                            {(() => {
                                const authorSlug = getPluginAuthorSlug(plugin);
                                const authorLabel = plugin.htmlStrippedAuthor || plugin.author;

                                if (!authorSlug) {
                                    return (
                                        <span className="text-xs text-gray-500 dark:text-gray-400">
                                            {authorLabel}
                                        </span>
                                    );
                                }

                                return (
                                    <Link
                                        href={getAuthorPageHref(authorSlug)}
                                        className="text-xs text-gray-500 dark:text-gray-400 hover:underline"
                                        onClick={(event) => {
                                            event.stopPropagation();
                                        }}
                                    >
                                        {authorLabel}
                                    </Link>
                                );
                            })()}
                        </div>
                    </div>
                ),
            },
            {
                id: 'price',
                label: 'Price',
                sortable: true,
                defaultDescending: false,
                sortType: 'number',
                contentClassName: "min-w-[6rem]",
                getSortValue: (plugin) => plugin.price?.amount ?? null,
                renderCell: (plugin) => formatPrice(plugin.price) || '—',
            },
            {
                id: 'rating',
                label: 'Rating',
                sortable: true,
                defaultDescending: true,
                sortType: 'number',
                contentClassName: "min-w-[8rem]",
                isFilterable: true,
                isFiltered: (minRatingFilterValue !== DEFAULT_MIN_RATING_FILTER_VALUE) || (maxRatingFilterValue !== DEFAULT_MAX_RATING_FILTER_VALUE),
                getSortValue: (plugin) => plugin.rating ?? 0,
                renderCell: (plugin) => (
                    <div className="flex items-center gap-2">
                        <StarRating rating={plugin.rating} maxRating={5} starSizeClassName="w-4 h-4"/>
                        <span className="text-xs text-gray-500 dark:text-gray-400">({plugin.num_ratings})</span>
                    </div>
                ),
            },
            {
                id: 'num_ratings',
                label: 'Reviews',
                sortable: true,
                defaultDescending: true,
                sortType: 'number',
                contentClassName: "min-w-[6rem]",
                getSortValue: (plugin) => plugin.num_ratings ?? 0,
                renderCell: (plugin) => formatNumber(plugin.num_ratings),
            },
            {
                id: 'downloaded',
                label: 'Downloads',
                sortable: true,
                defaultDescending: true,
                sortType: 'number',
                contentClassName: "min-w-[8rem]",
                isFilterable: true,
                isFiltered: (minDownloadsFilterValue !== DEFAULT_MIN_DOWNLOADS_FILTER_VALUE) || (maxDownloadsFilterValue !== DEFAULT_MAX_DOWNLOADS_FILTER_VALUE),
                getSortValue: (plugin) => plugin.downloaded ?? 0,
                renderCell: (plugin) => formatNumber(plugin.downloaded),
            },
            {
                id: 'active_installs',
                label: 'Active Installs',
                sortable: true,
                defaultDescending: true,
                sortType: 'number',
                contentClassName: "min-w-[8rem]",
                isFilterable: true,
                isFiltered: (minActiveInstallsFilterValue !== DEFAULT_MIN_ACTIVE_INSTALLS_FILTER_VALUE) || (maxActiveInstallsFilterValue !== DEFAULT_MAX_ACTIVE_INSTALLS_FILTER_VALUE),
                getSortValue: (plugin) => plugin.active_installs ?? 0,
                renderCell: (plugin) => formatNumber(plugin.active_installs),
            },
            {
                id: 'installs_per_rating',
                label: 'Installs / review',
                sortable: true,
                defaultDescending: true,
                sortType: 'number',
                contentClassName: "min-w-[8rem]",
                getSortValue: getInstallsPerRating,
                renderCell: (plugin) => formatNumber(getInstallsPerRating(plugin), 1),
            },
            {
                id: 'installs_per_day',
                label: 'Installs / day',
                sortable: true,
                defaultDescending: true,
                sortType: 'number',
                contentClassName: "min-w-[8rem]",
                getSortValue: getInstallsPerDay,
                renderCell: (plugin) => formatNumber(getInstallsPerDay(plugin), 1),
            },
            {
                id: 'downloads_per_rating',
                label: 'Downloads / review',
                sortable: true,
                defaultDescending: true,
                sortType: 'number',
                contentClassName: "min-w-[8rem]",
                getSortValue: getDownloadsPerRating,
                renderCell: (plugin) => formatNumber(getDownloadsPerRating(plugin), 1),
            },
            {
                id: 'downloads_per_day',
                label: 'Downloads / day',
                sortable: true,
                defaultDescending: true,
                sortType: 'number',
                contentClassName: "min-w-[8rem]",
                getSortValue: getDownloadsPerDay,
                renderCell: (plugin) => formatNumber(getDownloadsPerDay(plugin), 1),
            },
            {
                id: 'reviews_per_day',
                label: 'Reviews / day',
                sortable: true,
                defaultDescending: true,
                sortType: 'number',
                contentClassName: "min-w-[8rem]",
                getSortValue: getReviewsPerDay,
                renderCell: (plugin) => formatNumber(getReviewsPerDay(plugin), 2),
            },
            {
                id: 'activation_rate',
                label: 'Active / download',
                sortable: true,
                defaultDescending: true,
                sortType: 'number',
                contentClassName: "min-w-[8rem]",
                getSortValue: getActivationRate,
                renderCell: (plugin) => {
                    const rate = getActivationRate(plugin);
                    if (rate === null || rate === undefined || !isFinite(rate)) {
                        return '—';
                    }
                    return `${(rate * 100).toFixed(1)}%`;
                },
            },
            {
                id: 'age',
                label: 'Age',
                sortable: true,
                defaultDescending: false,
                sortType: 'number',
                contentClassName: "min-w-[6rem]",
                isFilterable: true,
                isFiltered: (minAgeFilterValue > DEFAULT_MIN_AGE_FILTER_VALUE) || (maxAgeFilterValue < DEFAULT_MAX_AGE_FILTER_VALUE),
                getSortValue: getAgeDays,
                renderCell: (plugin) => formatAgeLabel(getAgeDays(plugin)),
            },
            {
                id: 'last_updated',
                label: 'Last Updated',
                sortable: true,
                defaultDescending: true,
                sortType: 'date',
                contentClassName: "min-w-[8rem]",
                isFilterable: true,
                isFiltered: (minLastUpdatedFilterValue !== DEFAULT_MIN_LAST_UPDATED_FILTER_VALUE) || (maxLastUpdatedFilterValue !== DEFAULT_MAX_LAST_UPDATED_FILTER_VALUE),
                getSortValue: (plugin) => plugin.last_updated,
                renderCell: (plugin) => formatDate(plugin.last_updated),
            },
            {
                id: 'tags',
                label: 'Tags',
                sortable: true,
                defaultDescending: true,
                sortType: 'number',
                contentClassName: "min-w-[10rem]",
                cellClassName: "min-w-[200px]",
                isFilterable: true,
                isFiltered: tagsFilterValues.length > 0,
                getSortValue: getTagSortValue,
                renderCell: (plugin) => (
                    <div className="flex flex-wrap items-center gap-2">
                        {plugin.tags && [...plugin.tags].sort((a, b) => (b.count ?? 0) - (a.count ?? 0)).slice(0, 3).map((tag) =>
                            <Chip
                                key={tag.slug}
                                value={tag.label}
                                slug={tag.slug}
                                onClick={(event) => {
                                    event?.preventDefault();
                                    event?.stopPropagation();
                                    if (!tagsFilterValues.includes(tag.slug)) {
                                        setTagsFilterValues([...tagsFilterValues, tag.slug]);
                                    } else {
                                        setTagsFilterValues(tagsFilterValues.filter((value) => value !== tag.slug));
                                    }
                                }}
                                selected={tagsFilterValues.includes(tag.slug)}
                                className="text-xs font-medium cursor-pointer"
                            />)}
                        {plugin.tags && plugin.tags.length > 3 &&
                            <span className="text-xs text-gray-500 dark:text-gray-400">+{plugin.tags.length - 3}</span>}
                    </div>
                ),
            },
            {
                id: 'languages',
                label: 'Languages',
                sortable: true,
                defaultDescending: true,
                sortType: 'number',
                contentClassName: "min-w-[10rem]",
                cellClassName: "min-w-[200px]",
                isFilterable: true,
                isFiltered: languagesFilterValues.length > 0,
                getSortValue: getLanguageSortValue,
                renderCell: (plugin) => (
                    <div className="flex flex-wrap items-center gap-2">
                        {plugin.languages && [...plugin.languages].sort((a, b) => (b.count ?? 0) - (a.count ?? 0)).slice(0, 3).map((language) =>
                            <Chip
                                key={language.slug}
                                value={language.label}
                                slug={language.slug}
                                onClick={(event) => {
                                    event?.preventDefault();
                                    event?.stopPropagation();
                                    if (!languagesFilterValues.includes(language.slug)) {
                                        setLanguagesFilterValues([...languagesFilterValues, language.slug]);
                                    } else {
                                        setLanguagesFilterValues(languagesFilterValues.filter((value) => value !== language.slug));
                                    }
                                }}
                                selected={languagesFilterValues.includes(language.slug)}
                                className="text-xs font-medium cursor-pointer"
                            />)}
                        {plugin.languages && plugin.languages.length > 3 &&
                            <span className="text-xs text-gray-500 dark:text-gray-400">+{plugin.languages.length - 3}</span>}
                    </div>
                ),
            },
        ];
    }, [formatAgeLabel, formatNumber, formatPrice, getActivationRate, getAgeDays, getDownloadsPerDay, getDownloadsPerRating, getInstallsPerDay, getInstallsPerRating, getLanguageSortValue, getReviewsPerDay, getTagSortValue, handlePluginLinkClick, languagesFilterValues, maxActiveInstallsFilterValue, maxDownloadsFilterValue, maxLastUpdatedFilterValue, maxRatingFilterValue, minActiveInstallsFilterValue, minDownloadsFilterValue, minLastUpdatedFilterValue, minRatingFilterValue, nameFilterValue, searchFilterValue, setLanguagesFilterValues, setTagsFilterValues, tagsFilterValues, minAgeFilterValue, maxAgeFilterValue]);

    const sortableColumns = useMemo(
        () => tableColumns.filter((column) => column.sortable !== false),
        [tableColumns]
    );

    const sortingOptions = useMemo(() => (
        sortableColumns.flatMap((column) => [
            new TableSortingOption({
                slug: column.id,
                label: `${column.label} (${getSortDirectionLabel(column.sortType, true)})`,
                descending: true,
            }),
            new TableSortingOption({
                slug: column.id,
                label: `${column.label} (${getSortDirectionLabel(column.sortType, false)})`,
                descending: false,
            }),
        ])
    ), [sortableColumns]);

    const visibleTableColumnDefinitions = useMemo(() => tableColumns.filter((column) => visibleTableColumns.includes(column.id)), [tableColumns, visibleTableColumns]);

    const tableColumnsById = useMemo(() => {
        const map = new Map<TableColumnId | string, TableColumnDefinition>();
        tableColumns.forEach((column) => map.set(column.id, column));
        return map;
    }, [tableColumns]);

    const sortedTablePlugins = tablePluginsData;

    const renderLayoutButton = (option: PluginsLayout, label: string, Icon: React.ComponentType<{ className?: string }>) => (
        <button
            key={option}
            type="button"
            aria-pressed={layout === option}
            className={cx(
                "flex items-center gap-2 px-3 py-1 text-sm font-medium transition-colors",
                layout === option
                    ? "bg-gray-900 text-white dark:bg-gray-100 dark:text-gray-900 shadow-sm"
                    : "bg-white dark:bg-gray-800 text-gray-700 dark:text-gray-300 hover:bg-gray-100 dark:hover:bg-gray-700",
                option !== 'comfortable' ? "border-l border-gray-200 dark:border-gray-700" : "",
            )}
            onClick={() => setLayout(option)}
        >
            <Icon className="w-4 h-4"/>
            <span className="hidden sm:inline">{label}</span>
            <span className="sm:hidden capitalize">{label.charAt(0)}</span>
        </button>
    );

    const handleHeaderSort = (column: string, defaultDescending: boolean = false, sortable: boolean = true) => {
        if (!sortable) {
            return;
        }
        const nextDescending = sortColumn === column ? !sortDescending : defaultDescending;
        setSortColumn(column);
        setSortDescending(nextDescending);
    };

    const toggleTableColumn = useCallback((columnId: TableColumnId) => {
        if (REQUIRED_TABLE_COLUMNS.includes(columnId)) {
            return;
        }
        setVisibleTableColumns((current) => {
            const next = current.includes(columnId)
                ? current.filter((column) => column !== columnId)
                : [...current, columnId];
            const withRequired = Array.from(new Set([...REQUIRED_TABLE_COLUMNS, ...next]));
            return withRequired.length === 0 ? current : withRequired;
        });
    }, []);

    const resetVisibleTableColumns = useCallback(() => {
        setVisibleTableColumns(DEFAULT_TABLE_COLUMNS);
    }, []);

    useEffect(() => {
        if (layout !== 'table') {
            return;
        }
        if (!sortColumn || !API_SORTABLE_COLUMNS.has(sortColumn)) {
            if (sortColumn !== DEFAULT_SORT_COLUMN || sortDescending !== DEFAULT_SORT_DESCENDING) {
                setSortColumn(DEFAULT_SORT_COLUMN);
                setSortDescending(DEFAULT_SORT_DESCENDING);
            }
        }
    }, [layout, sortColumn, sortDescending]);

    useEffect(() => {
        if (layout !== 'table') {
            return;
        }
        const key = buildTableQueryKey();
        if (key !== tableQueryKey) {
            setTableQueryKey(key);
            setTablePluginsData([]);
            setPage(1);
        }
    }, [layout, buildTableQueryKey, tableQueryKey]);

    useEffect(() => {
        if (layout !== 'table') return;
        const sentinel = tableLoadMoreRef.current;
        if (!sentinel) return;

        const observer = new IntersectionObserver((entries) => {
            entries.forEach((entry) => {
                if (entry.isIntersecting && !loading && page < totalPages) {
                    if (tableLoadingRef.current) {
                        return;
                    }
                    tableLoadingRef.current = true;
                    setPage((prev) => prev + 1);
                }
            });
        }, {rootMargin: '200px'});

        observer.observe(sentinel);

        return () => {
            observer.disconnect();
        };
    }, [layout, loading, page, totalPages]);

    useEffect(() => {
        if (!loading) {
            tableLoadingRef.current = false;
        }
    }, [loading]);

    const filterChipConfigs: FilterChipConfig[] = useMemo(() => {
        const truncate = (value: string, fallback: string) => {
            if (!value) return fallback;
            return value.length > 40 ? `${value.slice(0, 37)}...` : value;
        };

        const summarizeRange = (min: number, max: number, defaultMin: number, defaultMax: number, formatter: (value: number) => string = (value) => formatNumber(value)) => {
            const hasMin = min > defaultMin;
            const hasMax = max < defaultMax;
            if (!hasMin && !hasMax) return "Any";
            if (hasMin && hasMax) return `${formatter(min)} - ${formatter(max)}`;
            if (hasMin) return `≥ ${formatter(min)}`;
            return `≤ ${formatter(max)}`;
        };

        const summarizeCollection = (values: string[], labelGetter: (slug: string) => string, fallback: string) => {
            if (!values.length) return fallback;
            const labels = values.map(labelGetter);
            if (labels.length === 1) return labels[0];
            if (labels.length === 2) return `${labels[0]}, ${labels[1]}`;
            return `${labels[0]}, ${labels[1]} +${labels.length - 2}`;
        };

        const formatDateValue = (value?: string) => {
            if (!value) return undefined;
            const parsed = new Date(value);
            if (Number.isNaN(parsed.getTime())) return undefined;
            return formatDateLocale(parsed);
        };

        const lastUpdatedSummary = () => {
            const minLabel = formatDateValue(minLastUpdatedFilterValue);
            const maxLabel = formatDateValue(maxLastUpdatedFilterValue);
            if (!minLabel && !maxLabel) return "Any time";
            if (minLabel && maxLabel) return `${minLabel} - ${maxLabel}`;
            if (minLabel) return `After ${minLabel}`;
            return `Before ${maxLabel}`;
        };

        const addedSummary = () => {
            const minLabel = formatDateValue(minAddedFilterValue);
            const maxLabel = formatDateValue(maxAddedFilterValue);
            if (!minLabel && !maxLabel) return "Any time";
            if (minLabel && maxLabel) return `${minLabel} - ${maxLabel}`;
            if (minLabel) return `After ${minLabel}`;
            return `Before ${maxLabel}`;
        };

        const ageSummary = () => summarizeRange(
            minAgeFilterValue,
            maxAgeFilterValue,
            DEFAULT_MIN_AGE_FILTER_VALUE,
            DEFAULT_MAX_AGE_FILTER_VALUE,
            (value) => `${value}y`
        );

        const tagsSummary = summarizeCollection(
            tagsFilterValues,
            (slug) => tagOptions.find((tag) => tag.slug === slug)?.label ?? slug,
            "Any tag"
        );

        const languagesSummary = summarizeCollection(
            languagesFilterValues,
            (slug) => languageOptions.find((language) => language.slug === slug)?.label ?? slug,
            "Any language"
        );

        const {column: activeSortColumn, descending: activeSortDescending} = getApiSort(sortColumn, sortDescending);
        const selectedSortingOption = sortingOptions.find((option) => option.getSlug() === activeSortColumn && option.getDescending() === activeSortDescending) || sortingOptions[0];
        const sortingSummary = selectedSortingOption?.getLabel() ?? "Sort plugins";

        return [
            {
                key: 'sort',
                label: 'Sort',
                summary: sortingSummary,
                active: activeSortColumn !== DEFAULT_SORT_COLUMN || activeSortDescending !== DEFAULT_SORT_DESCENDING,
                onClear: (activeSortColumn !== DEFAULT_SORT_COLUMN || activeSortDescending !== DEFAULT_SORT_DESCENDING)
                    ? () => {
                        setSortColumn(DEFAULT_SORT_COLUMN);
                        setSortDescending(DEFAULT_SORT_DESCENDING);
                    }
                    : undefined,
                children: (
                    <div className="space-y-3">
                        <p className="text-xs text-gray-500 dark:text-gray-400">Pick how plugins are ordered.</p>
                        <div className="flex flex-col gap-2">
                            {sortableColumns.map((column) => {
                                const ascendingLabel = getSortDirectionLabel(column.sortType, false);
                                const descendingLabel = getSortDirectionLabel(column.sortType, true);
                                const isAscendingActive = activeSortColumn === column.id && !activeSortDescending;
                                const isDescendingActive = activeSortColumn === column.id && activeSortDescending;
                                return (
                                    <div
                                        key={column.id}
                                        className="grid grid-cols-[minmax(0,1fr)_auto] items-center gap-3 rounded-md border border-gray-200 bg-white px-3 py-2 text-sm dark:border-gray-700 dark:bg-gray-800"
                                    >
                                        <span className="min-w-0 text-sm font-medium text-gray-800 dark:text-gray-200">
                                            {column.label}
                                        </span>
                                        <div className="flex items-center gap-2">
                                            <button
                                                type="button"
                                                aria-pressed={isAscendingActive}
                                                aria-label={`Sort ${column.label} ${ascendingLabel}`}
                                                className={cx(
                                                    "whitespace-nowrap rounded-md border px-2.5 py-1 text-[11px] font-semibold transition-colors",
                                                    isAscendingActive
                                                        ? "border-indigo-500 bg-indigo-50 text-indigo-700 shadow-sm dark:border-indigo-400 dark:bg-indigo-900/40 dark:text-indigo-50"
                                                        : "border-gray-200 bg-white text-gray-600 hover:border-indigo-300 hover:text-indigo-600 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-300 dark:hover:border-indigo-400 dark:hover:text-indigo-200"
                                                )}
                                                onClick={() => {
                                                    setSortColumn(column.id);
                                                    setSortDescending(false);
                                                }}
                                            >
                                                {ascendingLabel}
                                            </button>
                                            <button
                                                type="button"
                                                aria-pressed={isDescendingActive}
                                                aria-label={`Sort ${column.label} ${descendingLabel}`}
                                                className={cx(
                                                    "whitespace-nowrap rounded-md border px-2.5 py-1 text-[11px] font-semibold transition-colors",
                                                    isDescendingActive
                                                        ? "border-indigo-500 bg-indigo-50 text-indigo-700 shadow-sm dark:border-indigo-400 dark:bg-indigo-900/40 dark:text-indigo-50"
                                                        : "border-gray-200 bg-white text-gray-600 hover:border-indigo-300 hover:text-indigo-600 dark:border-gray-600 dark:bg-gray-900 dark:text-gray-300 dark:hover:border-indigo-400 dark:hover:text-indigo-200"
                                                )}
                                                onClick={() => {
                                                    setSortColumn(column.id);
                                                    setSortDescending(true);
                                                }}
                                            >
                                                {descendingLabel}
                                            </button>
                                        </div>
                                    </div>
                                );
                            })}
                        </div>
                    </div>
                ),
            },
            {
                key: 'search',
                label: 'Search',
                summary: truncate(searchFilterValue, 'Any keyword'),
                active: !!searchFilterValue,
                onClear: searchFilterValue ? () => setSearchFilterValue('') : undefined,
                children: (
                    <TextField
                        id="plugins-filter-search"
                        onChanged={(value) => setSearchFilterValue(value)}
                        className="w-full"
                        value={searchFilterValue}
                        placeholder="Search across name, description, tags..."
                    />
                ),
            },
            {
                key: 'name',
                label: 'Name',
                summary: truncate(nameFilterValue, 'Any name'),
                active: !!nameFilterValue,
                onClear: nameFilterValue ? () => setNameFilterValue('') : undefined,
                children: (
                    <TextField
                        id="plugins-filter-search-name"
                        onChanged={setNameFilterValue}
                        className="w-full"
                        value={nameFilterValue}
                        placeholder="Exact or partial name"
                    />
                ),
            },
            {
                key: 'description',
                label: 'Description',
                summary: truncate(descriptionFilterValue, 'Any description'),
                active: !!descriptionFilterValue,
                onClear: descriptionFilterValue ? () => setDescriptionFilterValue('') : undefined,
                children: (
                    <TextField
                        id="plugins-filter-search-description"
                        onChanged={setDescriptionFilterValue}
                        className="w-full"
                        value={descriptionFilterValue}
                        placeholder="Keywords in description"
                    />
                ),
            },
            {
                key: 'tags',
                label: 'Tags',
                summary: tagsSummary,
                active: tagsFilterValues.length > 0,
                onClear: tagsFilterValues.length ? () => setTagsFilterValues([]) : undefined,
                children: (
                    <MultiSelectField
                        id="plugins-filter-tags"
                        onChange={(values: string[]) => setTagsFilterValues(values)}
                        className="w-full"
                        options={tagOptions || []}
                        values={tagsFilterValues}
                        placeholder="Filter by tags"
                    />
                ),
            },
            {
                key: 'languages',
                label: 'Languages',
                summary: languagesSummary,
                active: languagesFilterValues.length > 0,
                onClear: languagesFilterValues.length ? () => setLanguagesFilterValues([]) : undefined,
                children: (
                    <MultiSelectField
                        id="plugins-filter-languages"
                        options={languageOptions || []}
                        onChange={(values: string[]) => setLanguagesFilterValues(values)}
                        className="w-full"
                        values={languagesFilterValues}
                        placeholder="Filter by languages"
                    />
                ),
            },
            {
                key: 'rating',
                label: 'Rating',
                summary: summarizeRange(minRatingFilterValue, maxRatingFilterValue, DEFAULT_MIN_RATING_FILTER_VALUE, DEFAULT_MAX_RATING_FILTER_VALUE, (value) => value.toFixed(1)),
                active: (minRatingFilterValue > DEFAULT_MIN_RATING_FILTER_VALUE) || (maxRatingFilterValue < DEFAULT_MAX_RATING_FILTER_VALUE),
                onClear: () => {
                    setMinRatingFilterValue(DEFAULT_MIN_RATING_FILTER_VALUE);
                    setMaxRatingFilterValue(DEFAULT_MAX_RATING_FILTER_VALUE);
                },
                children: (
                    <div className="flex flex-col gap-3">
                        <div className="flex flex-wrap items-center gap-2">
                            <span className="text-xs uppercase text-gray-500 dark:text-gray-400">Between</span>
                            <StarRatingFilter
                                className="inline-block w-fit"
                                placeholder="Min rating"
                                min={0}
                                max={5}
                                step={0.5}
                                value={minRatingFilterValue}
                                onChange={(e) => setMinRatingFilterValue(e)}
                                onReset={() => setMinRatingFilterValue(DEFAULT_MIN_RATING_FILTER_VALUE)}
                            />
                            <span className="text-xs uppercase text-gray-500 dark:text-gray-400">and</span>
                            <StarRatingFilter
                                className="inline-block w-fit"
                                placeholder="Max rating"
                                min={0}
                                max={5}
                                step={0.5}
                                value={maxRatingFilterValue}
                                onChange={(e) => setMaxRatingFilterValue(e)}
                                onReset={() => setMaxRatingFilterValue(DEFAULT_MAX_RATING_FILTER_VALUE)}
                            />
                        </div>
                        <p className="text-xs text-gray-500 dark:text-gray-400">Tap the stars or type a number to match TradingView-style quick tweaks.</p>
                    </div>
                ),
            },
            {
                key: 'downloads',
                label: 'Downloads',
                summary: summarizeRange(minDownloadsFilterValue, maxDownloadsFilterValue, DEFAULT_MIN_DOWNLOADS_FILTER_VALUE, DEFAULT_MAX_DOWNLOADS_FILTER_VALUE),
                active: (minDownloadsFilterValue > DEFAULT_MIN_DOWNLOADS_FILTER_VALUE) || (maxDownloadsFilterValue < DEFAULT_MAX_DOWNLOADS_FILTER_VALUE),
                onClear: () => {
                    setMinDownloadsFilterValue(DEFAULT_MIN_DOWNLOADS_FILTER_VALUE);
                    setMaxDownloadsFilterValue(DEFAULT_MAX_DOWNLOADS_FILTER_VALUE);
                },
                children: (
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <NumberFilter
                            className="w-full"
                            placeholder="Min downloads"
                            min={0}
                            max={DEFAULT_MAX_DOWNLOADS_FILTER_VALUE}
                            step={1000}
                            value={minDownloadsFilterValue}
                            acceptManualInput={true}
                            onChanged={(e) => setMinDownloadsFilterValue(e)}
                        />
                        <NumberFilter
                            className="w-full"
                            placeholder="Max downloads"
                            min={0}
                            max={DEFAULT_MAX_DOWNLOADS_FILTER_VALUE}
                            step={1000}
                            value={maxDownloadsFilterValue}
                            acceptManualInput={true}
                            onChanged={(e) => setMaxDownloadsFilterValue(e)}
                        />
                    </div>
                ),
            },
            {
                key: 'active_installs',
                label: 'Active installs',
                summary: summarizeRange(minActiveInstallsFilterValue, maxActiveInstallsFilterValue, DEFAULT_MIN_ACTIVE_INSTALLS_FILTER_VALUE, DEFAULT_MAX_ACTIVE_INSTALLS_FILTER_VALUE),
                active: (minActiveInstallsFilterValue > DEFAULT_MIN_ACTIVE_INSTALLS_FILTER_VALUE) || (maxActiveInstallsFilterValue < DEFAULT_MAX_ACTIVE_INSTALLS_FILTER_VALUE),
                onClear: () => {
                    setMinActiveInstallsFilterValue(DEFAULT_MIN_ACTIVE_INSTALLS_FILTER_VALUE);
                    setMaxActiveInstallsFilterValue(DEFAULT_MAX_ACTIVE_INSTALLS_FILTER_VALUE);
                },
                children: (
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <NumberFilter
                            className="w-full"
                            placeholder="Min installs"
                            min={0}
                            max={DEFAULT_MAX_ACTIVE_INSTALLS_FILTER_VALUE}
                            step={1000}
                            value={minActiveInstallsFilterValue}
                            acceptManualInput={true}
                            onChanged={(e) => setMinActiveInstallsFilterValue(e)}
                        />
                        <NumberFilter
                            className="w-full"
                            placeholder="Max installs"
                            min={0}
                            max={DEFAULT_MAX_ACTIVE_INSTALLS_FILTER_VALUE}
                            step={1000}
                            value={maxActiveInstallsFilterValue}
                            acceptManualInput={true}
                            onChanged={(e) => setMaxActiveInstallsFilterValue(e)}
                        />
                    </div>
                ),
            },
            {
                key: 'total_ratings',
                label: 'Total ratings',
                summary: summarizeRange(minTotalRatingsFilterValue, maxTotalRatingsFilterValue, DEFAULT_MIN_TOTAL_RATINGS_FILTER_VALUE, DEFAULT_MAX_TOTAL_RATINGS_FILTER_VALUE),
                active: (minTotalRatingsFilterValue > DEFAULT_MIN_TOTAL_RATINGS_FILTER_VALUE) || (maxTotalRatingsFilterValue < DEFAULT_MAX_TOTAL_RATINGS_FILTER_VALUE),
                onClear: () => {
                    setMinTotalRatingsFilterValue(DEFAULT_MIN_TOTAL_RATINGS_FILTER_VALUE);
                    setMaxTotalRatingsFilterValue(DEFAULT_MAX_TOTAL_RATINGS_FILTER_VALUE);
                },
                children: (
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <NumberFilter
                            className="w-full"
                            placeholder="Min ratings"
                            min={0}
                            max={DEFAULT_MAX_TOTAL_RATINGS_FILTER_VALUE}
                            step={10}
                            value={minTotalRatingsFilterValue}
                            acceptManualInput={true}
                            onChanged={(e) => setMinTotalRatingsFilterValue(e)}
                        />
                        <NumberFilter
                            className="w-full"
                            placeholder="Max ratings"
                            min={0}
                            max={DEFAULT_MAX_TOTAL_RATINGS_FILTER_VALUE}
                            step={10}
                            value={maxTotalRatingsFilterValue}
                            acceptManualInput={true}
                            onChanged={(e) => setMaxTotalRatingsFilterValue(e)}
                        />
                    </div>
                ),
            },
            {
                key: 'age',
                label: 'Age',
                summary: ageSummary(),
                active: (minAgeFilterValue > DEFAULT_MIN_AGE_FILTER_VALUE) || (maxAgeFilterValue < DEFAULT_MAX_AGE_FILTER_VALUE),
                onClear: () => {
                    setMinAgeFilterValue(DEFAULT_MIN_AGE_FILTER_VALUE);
                    setMaxAgeFilterValue(DEFAULT_MAX_AGE_FILTER_VALUE);
                },
                children: (
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <NumberFilter
                            className="w-full"
                            placeholder="Min age (years)"
                            min={DEFAULT_MIN_AGE_FILTER_VALUE}
                            max={DEFAULT_MAX_AGE_FILTER_VALUE}
                            step={0.5}
                            value={minAgeFilterValue}
                            acceptManualInput={true}
                            onChanged={(e) => setMinAgeFilterValue(e)}
                        />
                        <NumberFilter
                            className="w-full"
                            placeholder="Max age (years)"
                            min={DEFAULT_MIN_AGE_FILTER_VALUE}
                            max={DEFAULT_MAX_AGE_FILTER_VALUE}
                            step={0.5}
                            value={maxAgeFilterValue}
                            acceptManualInput={true}
                            onChanged={(e) => setMaxAgeFilterValue(e)}
                        />
                    </div>
                ),
            },
            {
                key: 'last_updated',
                label: 'Last updated',
                summary: lastUpdatedSummary(),
                active: (minLastUpdatedFilterValue !== DEFAULT_MIN_LAST_UPDATED_FILTER_VALUE) || (maxLastUpdatedFilterValue !== DEFAULT_MAX_LAST_UPDATED_FILTER_VALUE),
                onClear: () => {
                    setMinLastUpdatedFilterValue(DEFAULT_MIN_LAST_UPDATED_FILTER_VALUE);
                    setMaxLastUpdatedFilterValue(DEFAULT_MAX_LAST_UPDATED_FILTER_VALUE);
                },
                children: (
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <div className="flex flex-col gap-1">
                            <span className="text-xs uppercase text-gray-500 dark:text-gray-400">From</span>
                            <DatePicker
                                id="min-last-updated"
                                className="w-full"
                                value={minLastUpdatedFilterValue}
                                onChange={(value: string) => setMinLastUpdatedFilterValue(value)}
                                placeholder="Start date"
                            />
                        </div>
                        <div className="flex flex-col gap-1">
                            <span className="text-xs uppercase text-gray-500 dark:text-gray-400">To</span>
                            <DatePicker
                                id="max-last-updated"
                                className="w-full"
                                value={maxLastUpdatedFilterValue}
                                onChange={(value: string) => setMaxLastUpdatedFilterValue(value)}
                                placeholder="End date"
                            />
                        </div>
                    </div>
                ),
            },
            {
                key: 'added',
                label: 'Published',
                summary: addedSummary(),
                active: (minAddedFilterValue !== DEFAULT_MIN_ADDED_FILTER_VALUE) || (maxAddedFilterValue !== DEFAULT_MAX_ADDED_FILTER_VALUE),
                onClear: () => {
                    setMinAddedFilterValue(DEFAULT_MIN_ADDED_FILTER_VALUE);
                    setMaxAddedFilterValue(DEFAULT_MAX_ADDED_FILTER_VALUE);
                },
                children: (
                    <div className="grid grid-cols-1 gap-3 sm:grid-cols-2">
                        <div className="flex flex-col gap-1">
                            <span className="text-xs uppercase text-gray-500 dark:text-gray-400">From</span>
                            <DatePicker
                                id="min-added"
                                className="w-full"
                                value={minAddedFilterValue}
                                onChange={(value: string) => setMinAddedFilterValue(value)}
                                placeholder="Start date"
                            />
                        </div>
                        <div className="flex flex-col gap-1">
                            <span className="text-xs uppercase text-gray-500 dark:text-gray-400">To</span>
                            <DatePicker
                                id="max-added"
                                className="w-full"
                                value={maxAddedFilterValue}
                                onChange={(value: string) => setMaxAddedFilterValue(value)}
                                placeholder="End date"
                            />
                        </div>
                    </div>
                ),
            },
        ];
    }, [descriptionFilterValue, languageOptions, languagesFilterValues, maxActiveInstallsFilterValue, maxDownloadsFilterValue, maxLastUpdatedFilterValue, maxRatingFilterValue, maxTotalRatingsFilterValue, minActiveInstallsFilterValue, minDownloadsFilterValue, minLastUpdatedFilterValue, minRatingFilterValue, minTotalRatingsFilterValue, nameFilterValue, searchFilterValue, sortingOptions, sortableColumns, sortColumn, sortDescending, tagsFilterValues, tagOptions, formatNumber, minAddedFilterValue, maxAddedFilterValue, minAgeFilterValue, maxAgeFilterValue]);

    const nichesContent = (
        <div className="flex flex-col gap-3 sm:flex-row sm:flex-wrap sm:items-center md:gap-4">
            <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Niches</span>
            {nichePresets.map((preset) => (
                <div key={preset.id} className="relative group">
                    <button
                        type="button"
                        onClick={() => applyPreset(preset)}
                        className="inline-flex items-center gap-2 rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 shadow-sm transition hover:border-indigo-400 hover:text-indigo-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-100 dark:hover:border-indigo-400 dark:hover:text-indigo-200 dark:focus-visible:ring-indigo-400"
                    >
                        <span className="h-2 w-2 rounded-full bg-indigo-500 dark:bg-indigo-400" aria-hidden/>
                        <span>{preset.title}</span>
                    </button>
                    <div className="pointer-events-none absolute left-0 top-full z-30 mt-2 w-72 rounded-lg border border-gray-200 bg-white p-3 text-left shadow-2xl opacity-0 translate-y-1 transition duration-150 ease-out group-hover:translate-y-0 group-hover:opacity-100 group-focus-within:translate-y-0 group-focus-within:opacity-100 dark:border-gray-700 dark:bg-gray-900">
                        <p className="text-sm font-semibold text-gray-900 dark:text-gray-100">{preset.title}</p>
                        <p className="mt-1 text-xs text-gray-600 dark:text-gray-400">{preset.description}</p>
                        <div className="mt-2 flex flex-wrap gap-1">
                            {preset.pills.map((pill) => (
                                <span
                                    key={pill}
                                    className="rounded-full border border-indigo-100 bg-indigo-50 px-2 py-1 text-[11px] font-medium text-indigo-700 dark:border-indigo-700 dark:bg-indigo-900/50 dark:text-indigo-200"
                                >
                                    {pill}
                                </span>
                            ))}
                        </div>
                        <p className="mt-2 text-[11px] font-semibold text-indigo-600 dark:text-indigo-400">Click to apply</p>
                    </div>
                </div>
            ))}
        </div>
    );

    const filtersPanelContent = (
        <div className="flex flex-col gap-4">
            <div className="flex flex-col gap-3 md:flex-row md:items-center md:justify-between">
                <div className="flex flex-col gap-3 md:flex-row md:flex-wrap md:items-center md:gap-4 flex-1 min-w-0">
                    <h2 className="text-lg font-semibold text-gray-900 dark:text-gray-100">Filters</h2>
                    <FilterChipGroup filters={filterChipConfigs} className="w-full md:flex-1 min-w-0"/>
                </div>
                <ResetFilterButton
                    className="inline-block self-start md:self-auto"
                    askConfirmation={(countUsedFilters() > 1)}
                    onReset={() => resetFilters()}
                    enabled={countUsedFilters() > 0}
                />
            </div>
            {nichesContent}
        </div>
    );

    const filtersModalContent = (
        <div className="flex flex-col gap-4">
            <FilterChipGroup
                filters={filterChipConfigs}
                className="grid w-full grid-cols-1 items-stretch gap-3"
            />
            {nichesContent}
        </div>
    );

    return (
        <>
            {loading && <Loading/>}
            <div className="mx-auto w-full max-w-7xl p-4 pb-24 md:p-8 md:pb-8 flex flex-col gap-6">
                <header className="flex flex-col gap-4">
                    <div className="flex flex-col gap-4 md:flex-row md:items-end md:justify-between">
                        <div className="flex flex-col gap-2">
                            <h1 className="text-3xl md:text-5xl font-bold tracking-tight text-gray-900 dark:text-gray-100">
                                All plugins
                            </h1>
                            <p className="text-gray-700 dark:text-gray-300">
                                Search the WordPress and WooCommerce plugin marketplaces with advanced filters, niche presets, and sortable stats.
                            </p>
                        </div>
                        <div className="flex flex-col items-start gap-2 sm:flex-row sm:flex-wrap sm:items-center md:justify-end">
                            <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Layout</span>
                            <div className="flex items-center rounded-md border border-gray-200 dark:border-gray-700 overflow-hidden bg-white dark:bg-gray-800 shadow-sm">
                                {renderLayoutButton('comfortable', 'Comfortable', FaThLarge)}
                                {renderLayoutButton('compact', 'Compact', FaListUl)}
                                {renderLayoutButton('table', 'Table', FaTable)}
                            </div>
                        </div>
                    </div>
                    <div className="flex flex-wrap items-center justify-between gap-2 text-sm text-gray-600 dark:text-gray-400">
                        <span>{totalPlugins ? `${formatNumberLocale(totalPlugins)} plugins` : 'Browse plugins'}</span>
                        <span>{countUsedFilters()} active {countUsedFilters() === 1 ? 'filter' : 'filters'}</span>
                    </div>
                </header>

                <div
                    ref={filtersPanelRef}
                    className="hidden md:block w-full rounded-2xl border border-gray-200 dark:border-gray-700 bg-white/80 dark:bg-gray-900/40 shadow-sm p-4 md:p-5"
                >
                    {filtersPanelContent}
                </div>

                <div className="fixed inset-x-0 bottom-4 z-40 flex justify-center md:hidden">
                    <div className="flex max-w-[calc(100vw-2rem)] items-center gap-3 rounded-full border border-gray-200/70 bg-white/80 px-4 py-2 shadow-lg backdrop-blur dark:border-gray-700/70 dark:bg-gray-900/70">
                        <button
                            type="button"
                            onClick={() => setIsFiltersModalOpen(true)}
                            className="flex items-center gap-2 text-sm font-semibold text-gray-800 dark:text-gray-100"
                        >
                            <IoFilterCircleSharp className="h-5 w-5 text-indigo-600 dark:text-indigo-300"/>
                            <span>Filters</span>
                            <span className="text-xs font-semibold text-indigo-600 dark:text-indigo-300">
                                ({countUsedFilters()})
                            </span>
                        </button>
                        <span className="h-4 w-px bg-gray-200/80 dark:bg-gray-700/80" aria-hidden/>
                        <ResetFilterButton
                            className="px-2 py-1 text-xs"
                            askConfirmation={(countUsedFilters() > 1)}
                            onReset={() => resetFilters()}
                            enabled={countUsedFilters() > 0}
                        />
                    </div>
                </div>

                <FiltersModal
                    onClose={() => setIsFiltersModalOpen(false)}
                    onResetFilters={() => resetFilters()}
                    isOpen={isFiltersModalOpen}
                >
                    <ModalHeader onClose={() => setIsFiltersModalOpen(false)}>
                        <div className="flex w-full items-center justify-between gap-3">
                            <div className="flex items-center gap-3">
                                <IoFilterCircleSharp className="h-6 w-6 text-indigo-600 dark:text-indigo-300"/>
                                <div className="flex flex-col">
                                    <span className="text-lg font-semibold text-gray-900 dark:text-gray-100">Filters</span>
                                    <span className="text-xs text-gray-500 dark:text-gray-400">
                                        {countUsedFilters()} active
                                    </span>
                                </div>
                            </div>
                            <ResetFilterButton
                                className="px-2 py-1 text-xs"
                                askConfirmation={(countUsedFilters() > 1)}
                                onReset={() => resetFilters()}
                                enabled={countUsedFilters() > 0}
                            />
                        </div>
                    </ModalHeader>
                    <ModalBody className="space-y-4">
                        {filtersModalContent}
                    </ModalBody>
                </FiltersModal>
                <div className={cx("relative flex flex-col", layout === 'table' ? "overflow-x-auto" : "")}>
                {layout === 'table' && (
                    <div className="flex flex-col gap-3">
                        <div className="flex flex-wrap items-center justify-between gap-2 px-1">
                            <div className="text-xs text-gray-600 dark:text-gray-400">
                                Choose columns and click any header to sort.
                            </div>
                            <div className="relative" ref={columnSelectorRef}>
                                <button
                                    type="button"
                                    className={cx(
                                        "flex items-center gap-2 px-3 py-2 text-sm font-medium rounded-md border shadow-sm transition-colors",
                                        isColumnSelectorOpen
                                            ? "bg-gray-900 text-white border-gray-900 dark:bg-gray-100 dark:text-gray-900"
                                            : "bg-white text-gray-700 border-gray-200 dark:bg-gray-800 dark:text-gray-200 dark:border-gray-700 hover:bg-gray-50 dark:hover:bg-gray-700"
                                    )}
                                    onClick={() => setIsColumnSelectorOpen((prev) => !prev)}
                                >
                                    <FaColumns className="w-4 h-4"/>
                                    <span>Columns</span>
                                </button>
                                {isColumnSelectorOpen && (
                                    <div className="absolute right-0 mt-2 w-64 rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900 shadow-xl z-20">
                                        <div className="flex items-center justify-between px-3 py-2 border-b border-gray-200 dark:border-gray-700">
                                            <span className="text-sm font-semibold text-gray-900 dark:text-gray-100">Visible columns</span>
                                            <button
                                                type="button"
                                                className="text-xs font-medium text-indigo-600 dark:text-indigo-400 hover:underline"
                                                onClick={resetVisibleTableColumns}
                                            >
                                                Reset
                                            </button>
                                        </div>
                                        <div className="max-h-64 overflow-auto py-1">
                                            {tableColumns.map((column) => {
                                                const locked = REQUIRED_TABLE_COLUMNS.includes(column.id);
                                                return (
                                                    <label
                                                        key={column.id}
                                                        className="flex items-center justify-between px-3 py-2 gap-2 text-sm text-gray-800 dark:text-gray-200 hover:bg-gray-50 dark:hover:bg-gray-800 cursor-pointer"
                                                    >
                                                        <div className="flex items-center gap-2">
                                                            <input
                                                                type="checkbox"
                                                                className="rounded"
                                                                checked={visibleTableColumns.includes(column.id)}
                                                                disabled={locked}
                                                                onChange={() => toggleTableColumn(column.id)}
                                                            />
                                                            <span>{column.label}</span>
                                                        </div>
                                                        {locked && <span className="text-[11px] uppercase text-gray-400">Locked</span>}
                                                    </label>
                                                );
                                            })}
                                        </div>
                                    </div>
                                )}
                            </div>
                        </div>
                        {tablePluginsData.length > 0 && (
                            <Table
                                className={cx("rounded-lg border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-900")}
                                sorting={{column: sortColumn, descending: sortDescending}}
                                onTableSortingChange={({column, descending}) => {
                                    setSortColumn(column);
                                    setSortDescending(descending);
                                }}
                                page={page}
                                totalPages={totalPages}
                                onPageChange={handlePageChange}
                                loading={loading}
                            >
                                <TableHeader
                                    className={["bg-gray-100 text-gray-600 dark:bg-gray-800 dark:text-gray-300 text-sm font-medium tracking-wider", "sticky top-0 z-10"].join(' ')}
                                    columns={visibleTableColumnDefinitions.map((column) => (
                                        <TableHeaderColumn
                                            key={column.id}
                                            className={column.headerClassName ?? ""}
                                            label={column.label}
                                            onClick={() => handleHeaderSort(column.id, column.defaultDescending ?? false, column.sortable !== false)}
                                            contentClassName={column.contentClassName ?? ""}
                                            isSorted={sortColumn === column.id}
                                            isSortedDescending={sortColumn === column.id ? sortDescending : (column.defaultDescending ?? false)}
                                            onFilterButtonClick={column.isFilterable === false ? undefined : () => scrollToFilters()}
                                            isFiltered={column.isFiltered}
                                            isFilterable={column.isFilterable}
                                            isSortable={column.sortable !== false}
                                        />
                                    ))}
                                />
                                <TableBody
                                    className="bg-white dark:bg-gray-900 text-gray-700 dark:text-gray-200 text-sm"
                                    rows={(sortedTablePlugins || []).map((plugin) => <TableRow
                                        key={plugin.pluginid}
                                        className="hover:bg-gray-50 dark:hover:bg-gray-800"
                                        onMouseEnter={() => prefetchPlugin(plugin.pluginid)}
                                        columns={visibleTableColumnDefinitions.map((column) => (
                                            <TableCell
                                                key={`${plugin.pluginid}-${column.id}`}
                                                className={cx(column.cellClassName ?? "")}
                                            >
                                                {column.renderCell(plugin)}
                                            </TableCell>
                                        ))}
                                    />)}
                                />
                            </Table>
                        )}
                        <div ref={tableLoadMoreRef} className="h-6 w-full"/>
                        {!loading && tablePluginsData.length === 0 && (
                            <NoResults
                                className={['md:col-span-12', 'col-span-12', 'mx-3', "h-full",].join(' ')}
                                onResetFilters={() => resetFilters()}
                            />
                        )}
                        {page >= totalPages && tablePluginsData.length > 0 && (
                            <div className="text-center text-xs text-gray-500 dark:text-gray-400 pb-4">End of results</div>
                        )}
                    </div>
                )}
                {pluginsData && pluginsData.length > 0 && layout !== 'table' && (
                    <div className="flex flex-col gap-2 grow-0">
                        {(pluginsData || []).map((plugin, index) => {
                            const marketplace = plugin.marketplace;
                            const marketplaceLabel = getMarketplaceLabel(marketplace);
                            const marketplaceListingLabel = getMarketplaceListingLabel(marketplace);
                            const MarketplaceIcon = marketplace === 'woocommerce' ? SiWoocommerce : DiWordpress;
                            const priceLabel = formatPrice(plugin.price);
                            const authorProfileTitle = marketplace === 'woocommerce' ? 'Open vendor profile' : 'Open WordPress.org profile';
                            const minWordPressVersion = plugin.minimum_wordpress_version;
                            const minPhpVersion = plugin.minimum_php_version;
                            const showWpVersion = marketplace === 'wordpress'
                                && !!minWordPressVersion
                                && minWordPressVersion.match(/^[0-9.]+$/);
                            const showPhpVersion = marketplace === 'wordpress'
                                && !!minPhpVersion
                                && minPhpVersion.match(/^[0-9.]+$/);

                            return layout === 'comfortable' ? (
                                <div className="py-2" key={plugin.pluginid}
                                     id={plugin.pluginid}>
                                    <div
                                        className={cx(
                                            "group w-full",
                                            "flex flex-row items-start justify-start",
                                            "p-4 md:p-5 gap-4",
                                            "rounded-2xl border border-gray-200 dark:border-gray-700",
                                            "bg-white/80 dark:bg-gray-900/40",
                                            "shadow-sm hover:shadow-md transition-shadow",
                                        )}
                                        onMouseEnter={() => prefetchPlugin(plugin.pluginid)}
                                    >
                                        <div
                                            className={cx("flex flex-col items-start md:items-center justify-center gap-2",)}>
                                            <div
                                                className={cx(
                                                    "relative",
                                                    "w-14 h-14 md:w-16 md:h-16",
                                                    "rounded-xl overflow-hidden",
                                                    "border border-gray-200 dark:border-gray-700",
                                                    "bg-gray-100 dark:bg-gray-800",
                                                )}
                                                title={pluginsData[index].name}
                                            >
                                                {pluginsData[index].icons?.length > 0 ? (
                                                    <Image
                                                        src={pluginsData[index].icons?.[0]?.url}
                                                        alt={pluginsData[index].name}
                                                        fill
                                                        className="object-contain"
                                                        unoptimized={true}
                                                    />
                                                ) : (
                                                    <div className="w-full h-full flex items-center justify-center">
                                                        <FaPlug className="w-7 h-7 text-gray-400 dark:text-gray-500" aria-hidden="true"/>
                                                    </div>
                                                )}
                                            </div>
                                        </div>
                                        <div
                                            className={cx("flex flex-col justify-between gap-2", "w-full h-full",)}>
                                            <div
                                                className={cx("first-row flex flex-col md:flex-row gap-2 justify-start items-start", "w-full",)}>
                                                    <Link
                                                        href={`/plugins/${pluginsData[index].pluginid}`}
                                                        onClick={handlePluginLinkClick}
                                                        className={cx("plugin-name", "text-base md:text-lg font-semibold", "text-gray-900 dark:text-gray-100", "order-1 md:order-1", "group-hover:text-indigo-700 dark:group-hover:text-indigo-300", "hover:underline")}
                                                        title={pluginsData[index].name}
                                                    >
                                                        {pluginsData[index].name}
                                                    </Link>
                                                <div
                                                    className={cx("flex flex-row items-center justify-start gap-2", "order-3 md:order-2",)}>
                                                    <StarRating
                                                        rating={pluginsData[index].rating}
                                                        maxRating={5}
                                                        starSizeClassName="w-4 h-4"
                                                        className={cx("rating")}/>
                                                    <div
                                                        className={cx("text-sm font-medium", "text-gray-600 dark:text-gray-400")}>({pluginsData[index].num_ratings})
                                                    </div>
                                                </div>

                                                <div
                                                    className={cx("plugin-author", "text-sm font-medium", "text-gray-600 dark:text-gray-400", "order-2 md:order-3", "md:ml-auto",)}>
                                                    <div
                                                        className={cx("flex flex-row items-center justify-center w-fit-content h-full", "text-sm font-medium", "text-gray-800 dark:text-gray-200",)}
                                                    >
                                                        {(() => {
                                                            const authorSlug = getPluginAuthorSlug(pluginsData[index]);
                                                            if (!authorSlug) {
                                                                return <span>{pluginsData[index].author}</span>;
                                                            }

                                                            return (
                                                                <Link
                                                                    href={getAuthorPageHref(authorSlug)}
                                                                    onClick={(event) => {
                                                                        event.stopPropagation();
                                                                    }}
                                                                    className={cx("hover:underline")}
                                                                >
                                                                    {pluginsData[index].author}
                                                                </Link>
                                                            );
                                                        })()}
                                                        {pluginsData[index].author_profile.url && (
                                                            <a
                                                                href={pluginsData[index].author_profile.url}
                                                                target="_blank"
                                                                rel="noopener noreferrer"
                                                                onClick={(event) => {
                                                                    event.stopPropagation();
                                                                }}
                                                                className={cx("ml-1")}
                                                                title={authorProfileTitle}
                                                            >
                                                                <BiLinkExternal/>
                                                            </a>
                                                        )}
                                                    </div>
                                                </div>
                                            </div>

                                            <div className="flex flex-wrap items-center gap-2 text-xs">
                                                <span
                                                    className="inline-flex items-center gap-1 rounded-full bg-gray-100 dark:bg-gray-800 px-2.5 py-1 font-medium text-gray-700 dark:text-gray-200"
                                                    title={`${marketplaceListingLabel} listing`}
                                                >
                                                    <MarketplaceIcon className="w-3.5 h-3.5 text-gray-500 dark:text-gray-400" aria-hidden="true"/>
                                                    {marketplaceLabel}
                                                </span>
                                                {priceLabel && (
                                                    <span
                                                        className="inline-flex items-center gap-1 rounded-full bg-gray-100 dark:bg-gray-800 px-2.5 py-1 font-medium text-gray-700 dark:text-gray-200"
                                                        title={`Price ${priceLabel}`}
                                                    >
                                                        <FaTag className="w-3.5 h-3.5 text-gray-500 dark:text-gray-400" aria-hidden="true"/>
                                                        {priceLabel}
                                                    </span>
                                                )}
                                                <span
                                                    className="inline-flex items-center gap-1 rounded-full bg-gray-100 dark:bg-gray-800 px-2.5 py-1 font-medium text-gray-700 dark:text-gray-200"
                                                    title={`${formatNumber(pluginsData[index].active_installs)} active installs`}
                                                >
                                                    <FaPlug className="w-3.5 h-3.5 text-gray-500 dark:text-gray-400" aria-hidden="true"/>
                                                    {formatNumber(pluginsData[index].active_installs)} installs
                                                </span>
                                                <span
                                                    className="inline-flex items-center gap-1 rounded-full bg-gray-100 dark:bg-gray-800 px-2.5 py-1 font-medium text-gray-700 dark:text-gray-200"
                                                    title={`${formatNumber(pluginsData[index].downloaded)} downloads`}
                                                >
                                                    <FaDownload className="w-3.5 h-3.5 text-gray-500 dark:text-gray-400" aria-hidden="true"/>
                                                    {formatNumber(pluginsData[index].downloaded)} downloads
                                                </span>
                                                <span
                                                    className="inline-flex items-center gap-1 rounded-full bg-gray-100 dark:bg-gray-800 px-2.5 py-1 font-medium text-gray-700 dark:text-gray-200"
                                                    title={"Last updated " + formatDateTimeLocale(pluginsData[index].last_updated)}
                                                >
                                                    <FaHistory className="w-3.5 h-3.5 text-gray-500 dark:text-gray-400" aria-hidden="true"/>
                                                    {isFinite(new Date(pluginsData[index].last_updated).getTime()) && typeof now === "number"
                                                        ? `${Math.max(0, Math.floor((now - new Date(pluginsData[index].last_updated).getTime()) / (1000 * 3600 * 24)))}d ago`
                                                        : "—"}
                                                </span>
                                                <span
                                                    className="inline-flex items-center gap-1 rounded-full bg-gray-100 dark:bg-gray-800 px-2.5 py-1 font-medium text-gray-700 dark:text-gray-200"
                                                    title={"First added " + formatDateTimeLocale(pluginsData[index].added)}
                                                >
                                                    <FaCalendar className="w-3.5 h-3.5 text-gray-500 dark:text-gray-400" aria-hidden="true"/>
                                                    {isFinite(new Date(pluginsData[index].added).getTime()) && typeof now === "number"
                                                        ? `${((now - new Date(pluginsData[index].added).getTime()) / (1000 * 3600 * 24 * 365)).toFixed(1)}y old`
                                                        : "—"}
                                                </span>
                                                <span
                                                    className="hidden sm:inline-flex items-center gap-1 rounded-full bg-gray-100 dark:bg-gray-800 px-2.5 py-1 font-medium text-gray-700 dark:text-gray-200"
                                                    title={`${formatNumberLocale(pluginsData[index].contributors?.length ?? 0)} contributors`}
                                                >
                                                    <FaUsers className="w-3.5 h-3.5 text-gray-500 dark:text-gray-400" aria-hidden="true"/>
                                                    {formatNumberLocale(pluginsData[index].contributors?.length ?? 0)}
                                                </span>
                                                {showWpVersion && (
                                                    <span
                                                        className="hidden md:inline-flex items-center gap-1 rounded-full bg-gray-100 dark:bg-gray-800 px-2.5 py-1 font-medium text-gray-700 dark:text-gray-200"
                                                        title={"Requires WordPress " + minWordPressVersion}
                                                    >
                                                        <DiWordpress className="w-4 h-4 text-gray-500 dark:text-gray-400" aria-hidden="true"/>
                                                        WP ≥ {minWordPressVersion}
                                                    </span>
                                                )}
                                                {showPhpVersion && (
                                                    <span
                                                        className="hidden md:inline-flex items-center gap-1 rounded-full bg-gray-100 dark:bg-gray-800 px-2.5 py-1 font-medium text-gray-700 dark:text-gray-200"
                                                        title={"Requires PHP " + minPhpVersion}
                                                    >
                                                        <FaPhp className="w-4 h-4 text-gray-500 dark:text-gray-400" aria-hidden="true"/>
                                                        PHP ≥ {minPhpVersion}
                                                    </span>
                                                )}
                                            </div>
                                            <div
                                                className={cx("description flex flex-row gap-2 justify-start",)}>
                                                <div
                                                    className={cx("plugin-description", "text-sm", "text-gray-700 dark:text-gray-300", "line-clamp-2")}
                                                    title={pluginsData[index].htmlStrippedDescription?.slice(0, MAX_SHORT_DESCRIPTION_LENGTH) + (pluginsData[index].htmlStrippedDescription?.length ?? 0 > MAX_SHORT_DESCRIPTION_LENGTH ? "..." : "")}>
                                                    {pluginsData[index].htmlStrippedDescription?.slice(0, MAX_SHORT_DESCRIPTION_LENGTH) + (pluginsData[index].htmlStrippedDescription?.length ?? 0 > MAX_SHORT_DESCRIPTION_LENGTH ? "..." : "")}
                                                </div>
                                            </div>
                                            {pluginsData[index].tags?.length > 0 && (
                                                <div className="flex flex-wrap items-center gap-2">
                                                    <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Tags</span>
                                                    {pluginsData[index].tags.slice(0, 8).map((tag) => (
                                                        <Chip
                                                            key={tag.slug}
                                                            value={tag.label}
                                                            slug={tag.slug}
                                                            onClick={(event) => {
                                                                event?.preventDefault();
                                                                event?.stopPropagation();
                                                                if (!tagsFilterValues.includes(tag.slug)) {
                                                                    setTagsFilterValues([...tagsFilterValues, tag.slug]);
                                                                } else {
                                                                    setTagsFilterValues(tagsFilterValues.filter((value) => value !== tag.slug));
                                                                }
                                                            }}
                                                            selected={tagsFilterValues.includes(tag.slug)}
                                                            className="m-0"
                                                        />
                                                    ))}
                                                    {pluginsData[index].tags.length > 8 && (
                                                        <span className="text-xs text-gray-500 dark:text-gray-400">+{pluginsData[index].tags.length - 8}</span>
                                                    )}
                                                </div>
                                            )}
                                            {pluginsData[index].languages?.length > 0 && (
                                                <div className="flex flex-wrap items-center gap-2">
                                                    <span className="text-xs font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Languages</span>
                                                    {pluginsData[index].languages
                                                        .slice()
                                                        .sort((a, b) => (b.count ?? 0) - (a.count ?? 0))
                                                        .slice(0, 6)
                                                        .map((language) => (
                                                            <Chip
                                                                key={language.slug}
                                                                value={language.label}
                                                                slug={language.slug}
                                                                onClick={(event) => {
                                                                    event?.preventDefault();
                                                                    event?.stopPropagation();
                                                                    if (!languagesFilterValues.includes(language.slug)) {
                                                                        setLanguagesFilterValues([...languagesFilterValues, language.slug]);
                                                                    } else {
                                                                        setLanguagesFilterValues(languagesFilterValues.filter((value) => value !== language.slug));
                                                                    }
                                                                }}
                                                                selected={languagesFilterValues.includes(language.slug)}
                                                                className="m-0"
                                                            />
                                                        ))}
                                                    {pluginsData[index].languages.length > 6 && (
                                                        <span className="text-xs text-gray-500 dark:text-gray-400">+{pluginsData[index].languages.length - 6}</span>
                                                    )}
                                                </div>
                                            )}
                                            <div className="flex flex-wrap items-center gap-2">
                                                <Link
                                                    href={`/plugins/${pluginsData[index].pluginid}`}
                                                    onClick={handlePluginLinkClick}
                                                    className="inline-flex items-center justify-center rounded-full bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-indigo-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 dark:focus-visible:ring-indigo-400"
                                                >
                                                    Details
                                                </Link>
                                                    {pluginsData[index].wordpress_link.url && (
                                                        <a
                                                            href={pluginsData[index].wordpress_link.url}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            onClick={(event) => event.stopPropagation()}
                                                            className="inline-flex items-center justify-center rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 shadow-sm hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
                                                        >
                                                            <MarketplaceIcon className="w-3.5 h-3.5 mr-1" aria-hidden="true"/>
                                                            {marketplaceLabel}
                                                            <BiLinkExternal className="w-3.5 h-3.5 ml-1" aria-hidden="true"/>
                                                        </a>
                                                    )}
                                                {pluginsData[index].homepage && (
                                                    <a
                                                        href={pluginsData[index].homepage}
                                                        target="_blank"
                                                        rel="noopener noreferrer"
                                                        onClick={(event) => event.stopPropagation()}
                                                        className="inline-flex items-center justify-center rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 shadow-sm hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
                                                    >
                                                        Homepage <BiLinkExternal className="w-3.5 h-3.5 ml-1" aria-hidden="true"/>
                                                    </a>
                                                )}
                                            </div>
                                        </div>
                                    </div>
                                    {/*     If it's the last ittem, attach the navigation buttons */}
                                </div>
                            ) : (
                                <div className="py-2" key={plugin.pluginid} id={plugin.pluginid}>
                                    <div
                                        className={cx(
                                            "group w-full",
                                            "rounded-xl border border-gray-200 dark:border-gray-700",
                                            "bg-white/80 dark:bg-gray-900/40",
                                            "p-3 md:p-4",
                                            "shadow-sm hover:shadow-md transition-shadow",
                                        )}
                                        onMouseEnter={() => prefetchPlugin(plugin.pluginid)}
                                    >
                                        <div className="flex items-start gap-3 md:gap-4">
                                            <div
                                                className={cx(
                                                    "relative flex-shrink-0",
                                                    "w-12 h-12 md:w-14 md:h-14",
                                                    "overflow-hidden rounded-lg",
                                                    "border border-gray-200 dark:border-gray-700",
                                                    "bg-gray-100 dark:bg-gray-800",
                                                )}
                                                title={plugin.name}
                                            >
                                                {plugin.icons?.length > 0 ? (
                                                    <Image
                                                        src={plugin.icons?.[0]?.url}
                                                        alt={plugin.name}
                                                        fill
                                                        className="object-contain"
                                                        unoptimized={true}
                                                    />
                                                ) : (
                                                    <div className="w-full h-full flex items-center justify-center">
                                                        <FaPlug className="w-7 h-7 text-gray-400 dark:text-gray-500" aria-hidden="true"/>
                                                    </div>
                                                )}
                                            </div>
                                            <div className="flex flex-col gap-2 w-full">
                                                <div className="flex flex-wrap items-center gap-2">
                                                    <Link
                                                        href={`/plugins/${plugin.pluginid}`}
                                                        onClick={handlePluginLinkClick}
                                                        className="text-sm font-semibold text-gray-900 dark:text-gray-100 truncate group-hover:text-indigo-700 dark:group-hover:text-indigo-300 hover:underline"
                                                    >
                                                        {plugin.name}
                                                    </Link>
                                                    <div className="flex items-center gap-1 text-xs text-gray-600 dark:text-gray-400">
                                                        <StarRating rating={plugin.rating} maxRating={5} starSizeClassName="w-4 h-4"/>
                                                        <span>({plugin.num_ratings})</span>
                                                    </div>
                                                    {(() => {
                                                        const authorSlug = getPluginAuthorSlug(plugin);
                                                        const authorLabel = plugin.htmlStrippedAuthor || plugin.author;
                                                        const className = "text-xs text-gray-500 dark:text-gray-400 truncate max-w-[12rem]";

                                                        if (!authorSlug) {
                                                            return <span className={className}>by {authorLabel}</span>;
                                                        }

                                                        return (
                                                            <Link
                                                                href={getAuthorPageHref(authorSlug)}
                                                                className={cx(className, "hover:underline")}
                                                                onClick={(event) => {
                                                                    event.stopPropagation();
                                                                }}
                                                            >
                                                                by {authorLabel}
                                                            </Link>
                                                        );
                                                    })()}
                                                </div>
                                                <div className="flex flex-wrap items-center gap-2 text-xs">
                                                    <span
                                                        className="inline-flex items-center gap-1 rounded-full bg-gray-100 dark:bg-gray-800 px-2.5 py-1 font-medium text-gray-700 dark:text-gray-200"
                                                        title={`${marketplaceListingLabel} listing`}
                                                    >
                                                        <MarketplaceIcon className="w-3.5 h-3.5 text-gray-500 dark:text-gray-400" aria-hidden="true"/>
                                                        {marketplaceLabel}
                                                    </span>
                                                    {priceLabel && (
                                                        <span
                                                            className="inline-flex items-center gap-1 rounded-full bg-gray-100 dark:bg-gray-800 px-2.5 py-1 font-medium text-gray-700 dark:text-gray-200"
                                                            title={`Price ${priceLabel}`}
                                                        >
                                                            <FaTag className="w-3.5 h-3.5 text-gray-500 dark:text-gray-400" aria-hidden="true"/>
                                                            {priceLabel}
                                                        </span>
                                                    )}
                                                    <span
                                                        className="inline-flex items-center gap-1 rounded-full bg-gray-100 dark:bg-gray-800 px-2.5 py-1 font-medium text-gray-700 dark:text-gray-200"
                                                        title={`${formatNumber(plugin.active_installs)} active installs`}
                                                    >
                                                        <FaPlug className="w-3.5 h-3.5 text-gray-500 dark:text-gray-400" aria-hidden="true"/>
                                                        {formatNumber(plugin.active_installs)}
                                                    </span>
                                                    <span
                                                        className="inline-flex items-center gap-1 rounded-full bg-gray-100 dark:bg-gray-800 px-2.5 py-1 font-medium text-gray-700 dark:text-gray-200"
                                                        title={`${formatNumber(plugin.downloaded)} downloads`}
                                                    >
                                                        <FaDownload className="w-3.5 h-3.5 text-gray-500 dark:text-gray-400" aria-hidden="true"/>
                                                        {formatNumber(plugin.downloaded)}
                                                    </span>
                                                    <span
                                                        className="inline-flex items-center gap-1 rounded-full bg-gray-100 dark:bg-gray-800 px-2.5 py-1 font-medium text-gray-700 dark:text-gray-200"
                                                        title={"Last updated " + formatDateTimeLocale(plugin.last_updated)}
                                                    >
                                                        <FaHistory className="w-3.5 h-3.5 text-gray-500 dark:text-gray-400" aria-hidden="true"/>
                                                        {isFinite(new Date(plugin.last_updated).getTime()) && typeof now === "number"
                                                            ? `${Math.max(0, Math.floor((now - new Date(plugin.last_updated).getTime()) / (1000 * 3600 * 24)))}d`
                                                            : "—"}
                                                    </span>
                                                    <span
                                                        className="inline-flex items-center gap-1 rounded-full bg-gray-100 dark:bg-gray-800 px-2.5 py-1 font-medium text-gray-700 dark:text-gray-200"
                                                        title={"First added " + formatDateTimeLocale(plugin.added)}
                                                    >
                                                        <FaCalendar className="w-3.5 h-3.5 text-gray-500 dark:text-gray-400" aria-hidden="true"/>
                                                        {isFinite(new Date(plugin.added).getTime()) && typeof now === "number"
                                                            ? `${((now - new Date(plugin.added).getTime()) / (1000 * 3600 * 24 * 365)).toFixed(1)}y`
                                                            : "—"}
                                                    </span>
                                                </div>
                                                {plugin.htmlStrippedDescription && (
                                                    <p className="text-sm text-gray-700 dark:text-gray-300 line-clamp-2">
                                                        {plugin.htmlStrippedDescription?.slice(0, COMPACT_DESCRIPTION_LENGTH) + ((plugin.htmlStrippedDescription?.length ?? 0) > COMPACT_DESCRIPTION_LENGTH ? "..." : "")}
                                                    </p>
                                                )}
                                                {plugin.tags?.length > 0 && (
                                                    <div className="flex flex-wrap items-center gap-2">
                                                        <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Tags</span>
                                                        {plugin.tags.slice(0, 3).map((tag) => (
                                                            <Chip
                                                                key={tag.slug}
                                                                value={tag.label}
                                                                slug={tag.slug}
                                                                onClick={(event) => {
                                                                    event?.preventDefault();
                                                                    event?.stopPropagation();
                                                                    if (!tagsFilterValues.includes(tag.slug)) {
                                                                        setTagsFilterValues([...tagsFilterValues, tag.slug]);
                                                                    } else {
                                                                        setTagsFilterValues(tagsFilterValues.filter((value) => value !== tag.slug));
                                                                    }
                                                                }}
                                                                selected={tagsFilterValues.includes(tag.slug)}
                                                                className="m-0"
                                                            />
                                                        ))}
                                                        {plugin.tags.length > 3 && <span className="text-xs text-gray-500 dark:text-gray-400">+{plugin.tags.length - 3}</span>}
                                                    </div>
                                                )}
                                                {plugin.languages?.length > 0 && (
                                                    <div className="flex flex-wrap items-center gap-2">
                                                        <span className="text-[11px] font-semibold uppercase tracking-wide text-gray-500 dark:text-gray-400">Languages</span>
                                                        {plugin.languages.slice().sort((a, b) => (b.count ?? 0) - (a.count ?? 0)).slice(0, 3).map((language) => (
                                                            <Chip
                                                                key={language.slug}
                                                                value={language.label}
                                                                slug={language.slug}
                                                                onClick={(event) => {
                                                                    event?.preventDefault();
                                                                    event?.stopPropagation();
                                                                    if (!languagesFilterValues.includes(language.slug)) {
                                                                        setLanguagesFilterValues([...languagesFilterValues, language.slug]);
                                                                    } else {
                                                                        setLanguagesFilterValues(languagesFilterValues.filter((value) => value !== language.slug));
                                                                    }
                                                                }}
                                                                selected={languagesFilterValues.includes(language.slug)}
                                                                className="m-0"
                                                            />
                                                        ))}
                                                        {plugin.languages.length > 3 && <span className="text-xs text-gray-500 dark:text-gray-400">+{plugin.languages.length - 3}</span>}
                                                    </div>
                                                )}
                                                <div className="flex flex-wrap items-center gap-2">
                                                <Link
                                                    href={`/plugins/${plugin.pluginid}`}
                                                    onClick={handlePluginLinkClick}
                                                    className="inline-flex items-center justify-center rounded-full bg-indigo-600 px-3 py-1.5 text-xs font-semibold text-white shadow-sm hover:bg-indigo-700 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-indigo-500 dark:focus-visible:ring-indigo-400"
                                                >
                                                    Details
                                                </Link>
                                                    {plugin.wordpress_link.url && (
                                                        <a
                                                            href={plugin.wordpress_link.url}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            onClick={(event) => event.stopPropagation()}
                                                            className="inline-flex items-center justify-center rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 shadow-sm hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
                                                        >
                                                            <MarketplaceIcon className="w-3.5 h-3.5 mr-1" aria-hidden="true"/>
                                                            {marketplaceLabel}
                                                            <BiLinkExternal className="w-3.5 h-3.5 ml-1" aria-hidden="true"/>
                                                        </a>
                                                    )}
                                                    {plugin.homepage && (
                                                        <a
                                                            href={plugin.homepage}
                                                            target="_blank"
                                                            rel="noopener noreferrer"
                                                            onClick={(event) => event.stopPropagation()}
                                                            className="inline-flex items-center justify-center rounded-full border border-gray-200 bg-white px-3 py-1.5 text-xs font-semibold text-gray-700 shadow-sm hover:bg-gray-50 dark:border-gray-700 dark:bg-gray-800 dark:text-gray-200 dark:hover:bg-gray-700"
                                                        >
                                                            Homepage <BiLinkExternal className="w-3.5 h-3.5 ml-1" aria-hidden="true"/>
                                                        </a>
                                                    )}
                                                </div>
                                            </div>
                                        </div>
                                    </div>
                                </div>
                            )
                        })}
                        <div>
                            {page && totalPages && <PageNavigation
                                className="mt-2"
                                currentPage={page}
                                totalPages={totalPages}
                                totalItems={totalPlugins}
                                onPageChange={handlePageChange}
                                getPageHref={buildPluginsPageHref}
                            />}
                        </div>
                    </div>
                )}
                {pluginsData && pluginsData.length === 0 && layout !== 'table' && (
                    <NoResults
                        className={['md:col-span-12', 'col-span-12', 'mx-3', "h-full",].join(' ')}
                        onResetFilters={() => resetFilters()}
                    />
                )}
            </div>
        </div>
    </>);
}

export default PluginsList;
